import { setupNeedOf } from "@shared/connectors/setupNeed";
import {
  SkyButton,
  SkyMenu,
  SkyCard,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyText,
  SkyTextarea,
  SkyTooltip,
} from "@skypointcloud/skypoint-ui";
import { keepEditorForCreated } from "@/lib/connectionEditorKey";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useParams, useSearchParams } from "wouter";
import {
  RecordNotFound,
  isMalformedRecordId,
  isNotFoundError,
} from "@/components/access/RecordNotFound";
import {
  CONNECTOR_NOT_ALLOWED_MESSAGE,
  CONNECTOR_NOT_AVAILABLE_MESSAGE,
} from "@shared/types/connectorAccess";
import {
  ENTITY_PAGE_SIZE,
  type DataflowDetail,
  type DataflowEntityView,
  type DataflowSummary,
  type DataflowFieldInput,
  type EntitySelectionInput,
  type EntityTypeFilter,
  type LastValidation,
  type PatchEntitiesBody,
  type ValidatedEntity,
  VALIDATION_UNAVAILABLE_CODE,
  DESCRIPTION_MAX,
  DESCRIPTION_TOO_LONG,
  displayNameIssue,
  displayNameKey,
  DISPLAY_NAME_REQUIRED,
} from "@shared/types/dataflows";
import type { EntitiesQuery } from "@shared/types/entities";
import {
  CONNECTION_NAME_RULE,
  connectionNameFromDisplayName,
  effectiveTableNameCase,
  entityTableSegment,
  findTargetNameCollisions,
  foldForCase,
  isConnectionName,
  tableNameCollisionSentence,
  targetTableName,
  type TableNameCase,
  type TableNaming,
} from "@shared/warehouse/targetTableName";
import { WATCH_DEFAULT_OUTCOMES } from "@shared/types/notifications";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { AddEntityDrawer } from "@/components/connections/AddEntityDrawer";
import { ReportList } from "@/components/connections/reports/ReportList";
import {
  ReportDrawer,
  type ReportDrawerMode,
  type ReportDrawerSubmit,
} from "@/components/connections/reports/ReportDrawer";
import {
  RemoveReportDialog,
  type RemoveReportTarget,
  RebuildReportsDialog,
  landedReportEdits,
  rebuildSentences,
} from "@/components/connections/reports/reportDialogs";
import { DEFAULT_SCHEMA_POLICY } from "@shared/types/schemaChanges";
import { BrowseDialog } from "@/components/connections/BrowseDialog";
import { ConnectionRunsTab } from "@/components/connections/ConnectionRunsTab";
import {
  ConnectionStatusBadge,
  connectionStatusOf,
  connectionWaitingOf,
  waitingLine,
} from "@/components/connections/ConnectionStatus";
import { ConnectorLogo } from "@/components/connections/ConnectorLogo";
import { changedTargetFieldNames, joinFieldNames } from "@/components/connections/targetChange";
import {
  NO_REFUSAL_FIELD_ERRORS,
  refusalFieldErrors,
  refusalPanelLines,
  type RefusalSentBody,
  type RefusalFieldErrors,
} from "@/components/connections/saveRefusal";
import { TARGET_CHANGED_CODE } from "@shared/connectors/targetFields";
import { CREDENTIAL_MISSING_CODE } from "@shared/connectors/credentialFields";
import { orderConnectorVersions } from "@shared/connectors/versionOrder";
import {
  joinParts,
  reveal,
  revealField,
  VALIDATION_FAILED_CARD,
  VALIDATION_PASSED_CARD,
  validatedAtSentence,
  validationSentence,
  withFinalStop,
} from "@/lib/connections/validationText";
import { CredentialExpiryEditor } from "@/components/credentials/CredentialExpiryEditor";
import {
  CredentialAttentionBadge,
  CredentialCheckLine,
} from "@/components/credentials/CredentialStatus";
import { useConnectionToggle } from "@/components/connections/DisableConnectionDialog";
import { WatchBadge } from "@/components/notifications/WatchBadge";
import { useSetWatch } from "@/hooks/use-alert-routing";
import { ConnectorPicker } from "@/components/connections/ConnectorPicker";
import {
  EntityTable,
  entityHasLanded,
  type EntityNameCollision,
  type EntityRow,
  type EntityView,
  type ReportDefinitionValue,
} from "@/components/connections/EntityTable";
import { targetNameIssue } from "@/components/connections/TableNameLine";
import { FieldGroups } from "@/components/connections/FieldGroups";
import {
  ImportLiveLine,
  ImportNowButton,
  ImportOptionsButton,
  ImportStepText,
  LastRunPanel,
  importOffReason,
} from "@/components/connections/LastRunPanel";
import { destinationBlockReason, useDestinationReadiness } from "@/hooks/use-destination-readiness";
import {
  NOT_WHILE_VALIDATING,
  REVALIDATE_LABEL,
  SaveBar,
  UNSAVED_TEST_FAILED,
  UNSAVED_TEST_FAILED_SAVE,
  UNSAVED_TEST_PASSED_HELD,
  unsavedTestPassed,
  VALIDATE_CONNECTION_LABEL,
  type SaveBarValidationResult,
} from "@/components/connections/SaveBar";
import { NO_VERDICT_TITLE, isNoVerdict } from "@shared/connectors/noVerdict";
import { isReachFailure } from "@shared/connectors/reachDetails";
import { ReachDetails } from "@/components/connections/ReachDetails";
import { removedSelectedSentence } from "@/components/connections/ValidationWait";
import { SchemaChangeDrawer } from "@/components/connections/SchemaChangeDrawer";
import { SchemaPolicySection } from "@/components/connections/SchemaPolicySection";
import { decisionNeededSentence } from "@/components/connections/schemaChangeCopy";
import { TableRunDialog } from "@/components/connections/TableRunDialog";
import { RunNowDialog } from "@/components/runs/RunNowDialog";
import { useFieldDependencies } from "@/components/connections/useFieldDependencies";
import { inEntityOrder } from "@shared/connectors/entityOrder";
import { notAfterTodayIssue } from "@shared/connectors/fieldRules";
import { hasFieldErrors, useValidation } from "@/components/connections/useValidation";
import { encodeFieldValue, savedFieldValue } from "@/components/connections/fieldValues";
import type { ConnectorDefinition, FormValues } from "@/components/connections/types";
import { useConnectorDefinition } from "@/hooks/use-connectors";
import { credentialTarget, useCredentials, useSetDataflowExpiry } from "@/hooks/use-credentials";
import {
  useCreateDataflow,
  useDataflow,
  useDataflowEntities,
  useDataflows,
  useInvalidateDataflows,
  usePatchEntities,
  useRevalidateDataflow,
  useUpdateDataflow,
  useValidateDataflow,
  type EntityPage,
} from "@/hooks/use-dataflows";
import { usePermissions } from "@/hooks/use-permissions";
import { useDestinationSummary } from "@/hooks/use-binding";
import { useSchemaChangeSummary } from "@/hooks/use-schema-changes";
import { useTableRunsEnabled } from "@/hooks/use-server-build";
import {
  importStartNoticeKey,
  useConnectionImport,
  useImportStartRefusals,
  useStartImport,
} from "@/hooks/use-run-activity";
import {
  CONNECTION_REFRESH_LINE,
  CONNECTION_REFRESH_LINE_WHOLE_DAYS,
  FIRST_IMPORT_HINT,
  FULL_ONLY_IMPORT_NOTE,
  firstImportNote,
} from "@/lib/runs/refreshHint";
import { isActivityLive } from "@/lib/runs/runActivity";
import { ApiClientError, failureMessage } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useDismissNoticeWhen, useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { CancelIcon, ChevronLeftIcon, PauseIcon, PlayIcon, RetryIcon } from "@/lib/icons";
import {
  CancelImportDialog,
  NOT_WHILE_IMPORTING,
  SAVE_AFTER_IMPORT,
  type CancelImportTarget,
} from "@/components/connections/CancelImportDialog";
import { NEW_CONNECTION_PATH, ROUTES, absolutePath, connectionPath } from "@/lib/routes";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import {
  holdsPinnedValues,
  pinnedFieldsChanged,
  pinnedFieldsOf,
  withPinnedValues,
} from "@/components/connections/pinnedFields";

/**
 * The connection editor (UX-4, FR-40 to FR-46).
 *
 * Two panes, one job: describe the source on the left, choose what to import on
 * the right. Three rules run through every branch below and each of them is the
 * kind that is quietly implemented backwards:
 *
 * 1. **Save is allowed at any time** (FR-42). Before validation, after a failed
 *    validation, with nothing selected. The server recomputes `state`, so the
 *    screen never decides that a connection is a Draft — it reports it.
 * 2. **A stored credential is never re-typed and never re-sent** (FR-43). It
 *    arrives as `MASKED_VALUE` and, if the user leaves it alone, goes back as
 *    that same sentinel on a save — which is the server's documented "keep what
 *    is in the vault" signal, and is the ONLY reason the field is sent at all:
 *    the write replaces the whole field set, so omitting it would delete the
 *    row and orphan the secret. Re-validation, which replaces nothing, sends
 *    only the fields the user actually retyped.
 * 3. **The entity list belongs to the last successful validation** (FR-44), so
 *    every success refreshes it rather than merging into a stale one. A saved
 *    connection's test of values it does not hold changes nothing saved (#1098):
 *    not its state, not "Last validated", not its tables; a Save of the values it
 *    passed asks the server to test them again (`validate` on the update).
 * 4. **The catalogue is never held here** (legacy issues A1/A2). A saved
 *    connection's entities are paged and searched on the server; this screen
 *    keeps only the page it is showing and a DRAFT of what the user changed —
 *    ticks and per-entity settings keyed by name — which a save sends as a
 *    `PATCH` of deltas. "Select all matching" is the one entity action that
 *    writes immediately, because only the server can resolve "every row this
 *    search matches" without the list. A connection that is not saved yet has no
 *    server rows, so its catalogue is the validator's answer, held locally and
 *    paged the same way; it goes up whole on the create.
 * 5. **A save names the row it loaded** (G3). Every write carries the
 *    `updatedAt` this screen last saw; a 409 `STALE_UPDATE` means someone else
 *    saved first, and the screen says so and offers a reload rather than
 *    overwriting them.
 */

/** A per-entity setting the user changed on a saved connection, with the tick it goes with. */
interface EntitySettingsDraft {
  isSelected: boolean;
  incrementalEnabled: boolean;
  watermarkColumn: string | null;
  keyColumns: string[];
  /**
   * Lead round: the table's own warehouse name as typed; null clears a stored one. Absent, the
   * stored name is kept (the PATCH omits it).
   */
  targetName?: string | null;
  /** #2428: a GA4 report's definition as edited; absent keeps the stored one. */
  definition?: ReportDefinitionValue;
}

/**
 * The unsaved entity changes of a saved connection. `ticks` remembers the server's
 * value when a row was first touched, so the selected count can be derived without
 * the list and a tick that returns to what the server holds is no change at all.
 */
interface EntityDraft {
  /**
   * `displayName` is the row's as the tick was made, so "Select all N matching" knows a tick of a
   * row matched by its display name alone, on a page no longer shown (#1414). `view` is whether the
   * row is a view, so the Type filter and the view count reach a tick made on another page (#1465).
   */
  ticks: Record<
    string,
    { was: boolean; now: boolean; displayName?: string | null; view?: boolean }
  >;
  settings: Record<string, EntitySettingsDraft>;
  /** Hand-named entities (connectors with `supportsAddEntity`), not yet on the server. */
  added: EntityRow[];
  /**
   * #2428: saved hand-named entities (GA4 reports) to remove, as the row read when removed. A
   * save sends their removal first (R8), so a property they read can leave the property list.
   */
  removed: EntityRow[];
  /**
   * #2428: a saved report's definition as it was when first edited, and whether its table had
   * landed, so Save can ask before it rebuilds a table that holds data.
   */
  editedFrom?: Record<string, { was: ReportDefinitionValue; landed: boolean }>;
}

const EMPTY_DRAFT: EntityDraft = { ticks: {}, settings: {}, added: [], removed: [] };

interface EditorForm {
  /** The "Display name" field: free text, editable (instance 0014: `dataflows.display_name`). */
  displayName: string;
  /**
   * Round 6: the "Name" field (owner 2026-10-01 23:25), `dataflows.name` (instance 0014; the API
   * called it `tablePrefix` before), the first part of every table name
   * (`<NAME>_<SCHEMA>_<TABLE>`). A new connection's follows the Display name until it is edited;
   * a saved one's is the server's, read only.
   */
  name: string;
  /** True once the Name of a new connection was typed into; only then is it sent. */
  nameEdited: boolean;
  description: string;
  imageTag: string;
  refreshType: "full" | "incremental";
  values: FormValues;
  /** The whole catalogue, for a connection that is not saved yet; empty otherwise. */
  entities: EntityRow[];
  /** Unsaved entity changes of a saved connection; empty for a new one. */
  draft: EntityDraft;
}

/** The schema log's `initialStatus`, passed only when the opener chose one. */
function initialStatusProp(status: "open" | "all" | undefined): { initialStatus?: "open" | "all" } {
  return status ? { initialStatus: status } : {};
}

function draftIsEmpty(draft: EntityDraft): boolean {
  return (
    Object.keys(draft.ticks).length === 0 &&
    Object.keys(draft.settings).length === 0 &&
    draft.added.length === 0 &&
    draft.removed.length === 0
  );
}

/** #2428: the connection's history window in days (the GA4 seed's default is 90). */
function historyDaysOf(values: FormValues): number {
  const days = Number(values.historyDays);
  return Number.isInteger(days) && days > 0 ? days : 90;
}

/** #2428: a new report's row, ticked when added (R26). */
function reportRow(report: ReportDrawerSubmit): EntityRow {
  return {
    name: report.name,
    displayName: report.name,
    sourceSchema: null,
    sourceTable: report.name,
    isSelected: true,
    incrementalEnabled: false,
    watermarkColumn: null,
    keyColumns: [],
    definition: report.definition,
  };
}

/**
 * #2428 (review of #2442): Add with the name of a saved report removed in this draft. The removal
 * is undone and the add becomes an Edit of the stored row, ticked, so the list shows one row and
 * Save asks before it rebuilds a table that has loaded (`editedFrom`), as an Edit does.
 */
function readdRemoved(
  draft: EntityDraft,
  stored: EntityRow,
  report: ReportDrawerSubmit,
): EntityDraft {
  const editedFrom = draft.editedFrom ?? {};
  return {
    ...draft,
    removed: draft.removed.filter((removed) => removed.name !== report.name),
    ticks: stored.isSelected
      ? draft.ticks
      : { ...draft.ticks, [report.name]: { was: false, now: true } },
    settings: {
      ...draft.settings,
      [report.name]: {
        isSelected: true,
        incrementalEnabled: stored.incrementalEnabled,
        watermarkColumn: stored.watermarkColumn,
        keyColumns: stored.keyColumns,
        definition: report.definition,
      },
    },
    editedFrom: {
      ...editedFrom,
      [report.name]: editedFrom[report.name] ?? {
        was: stored.definition ?? report.definition,
        landed: entityHasLanded(stored),
      },
    },
  };
}

/** #2428: a report's definition as a write sends it, without what a reader was withheld. */
function definitionInput(
  definition: ReportDefinitionValue | undefined,
): EntitySelectionInput["definition"] {
  if (!definition) return undefined;
  return {
    propertyId: definition.propertyId ?? "",
    dimensions: definition.dimensions,
    metrics: definition.metrics,
    ...(definition.dimensionFilter ? { dimensionFilter: definition.dimensionFilter } : {}),
  };
}

/** Whether a connector's tables carry their own incremental setting (`supportsIncrementalPerEntity`). */
function entityIncrementalAllowed(definition: ConnectorDefinition): boolean {
  return (
    definition.supportsIncrementalPerEntity ||
    definition.entitySelection?.supportsIncrementalPerEntity === true
  );
}

/**
 * #1131: a connector that reads every table whole never stores a table's incremental setting or
 * watermark (the server refuses them), so they go up off and empty, whatever an older save left.
 */
function withoutIncremental<
  T extends { incrementalEnabled?: boolean; watermarkColumn?: string | null },
>(entities: T[], allowed: boolean): T[] {
  return allowed
    ? entities
    : entities.map((entity) => ({ ...entity, incrementalEnabled: false, watermarkColumn: null }));
}

/** The `PATCH /entities` body a draft becomes; `undefined` when there is nothing to send. */
function draftToPatch(
  draft: EntityDraft,
  incrementalAllowed = true,
): PatchEntitiesBody | undefined {
  if (draftIsEmpty(draft)) return undefined;
  const select = Object.entries(draft.ticks)
    .filter(([, tick]) => tick.now)
    .map(([name]) => name);
  const deselect = Object.entries(draft.ticks)
    .filter(([, tick]) => !tick.now)
    .map(([name]) => name);
  const entities: EntitySelectionInput[] = [
    ...Object.entries(draft.settings).map(([name, { definition, ...settings }]) => ({
      name,
      ...settings,
      ...(definition ? { definition: definitionInput(definition) } : {}),
    })),
    ...draft.added.map((row) => ({
      name: row.name,
      isSelected: row.isSelected,
      incrementalEnabled: row.incrementalEnabled,
      watermarkColumn: row.watermarkColumn,
      keyColumns: row.keyColumns,
      ...(row.targetName ? { targetName: row.targetName } : {}),
      ...(row.definition ? { definition: definitionInput(row.definition) } : {}),
    })),
  ];
  // A body with only a removal is sent before the connection save (R8), never here.
  if (select.length === 0 && deselect.length === 0 && entities.length === 0) return undefined;
  return {
    ...(select.length > 0 ? { select } : {}),
    ...(deselect.length > 0 ? { deselect } : {}),
    ...(entities.length > 0 ? { entities: withoutIncremental(entities, incrementalAllowed) } : {}),
  };
}

function toRow(entity: DataflowEntityView): EntityRow {
  return {
    name: entity.name,
    displayName: entity.displayName,
    sourceSchema: entity.sourceSchema,
    sourceTable: entity.sourceTable,
    isSelected: entity.isSelected,
    incrementalEnabled: entity.incrementalEnabled,
    watermarkColumn: entity.watermarkColumn,
    keyColumns: entity.keyColumns,
    ...(entity.entityType ? { entityType: entity.entityType } : {}),
    // Product gaps row 2: "Data through", when the table has landed at least once.
    ...(entity.refreshedThrough ? { refreshedThrough: entity.refreshedThrough } : {}),
    // Review r2: the case its first load fixed, which the duplicate check reads before the
    // destination's setting.
    ...(entity.tableNameCase ? { tableNameCase: entity.tableNameCase } : {}),
    // Round 7: its own warehouse name, which the duplicate check reads before the derived one.
    ...(entity.targetName ? { targetName: entity.targetName } : {}),
    // #2428: a GA4 report's definition, and whether its table waits for a rebuild.
    ...(entity.definition ? { definition: entity.definition } : {}),
    ...(entity.rebuildPending ? { rebuildPending: true } : {}),
  };
}

/** The listing's predicate, for the catalogue a new connection holds locally. */
function matches(
  row: Pick<EntityRow, "name" | "displayName" | "isSelected" | "entityType">,
  search: string,
  view: EntityView,
  entityType: EntityTypeFilter = "all",
): boolean {
  if (view === "selected" && !row.isSelected) return false;
  if (view === "unselected" && row.isSelected) return false;
  if (entityType === "view" && row.entityType !== "view") return false;
  if (entityType === "table" && row.entityType === "view") return false;
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [row.name, row.displayName].some((value) => value?.toLowerCase().includes(needle));
}

/**
 * #1414: a saved connection's draft without the ticks of the rows "Select all N matching" covers.
 * The server applies a name after a search (`patchEntitiesBodySchema`), so an unsaved untick of a
 * covered row would undo the button for that row: 19 of 20 saved under "20 matching selected".
 * A row is covered by the server's predicate (`entityFilter`): its stored tick (`was`) for the
 * view, its name or display name for the search, its type for the Type filter. Ticks of rows outside the match stay and are
 * saved with it.
 */
function draftOutsideMatch(
  draft: EntityDraft,
  search: string,
  view: EntityView,
  entityType: EntityTypeFilter = "all",
): EntityDraft {
  // A tick made inside the match is dropped even when it runs against the button (a tick under "Clear" on the unselected view): the button wins.
  const ticks = Object.fromEntries(
    Object.entries(draft.ticks).filter(
      ([name, tick]) =>
        !matches(
          {
            name,
            displayName: tick.displayName ?? null,
            isSelected: tick.was,
            entityType: tick.view ? "view" : "table",
          },
          search,
          view,
          entityType,
        ),
    ),
  );
  return { ...draft, ticks };
}

/**
 * The editor's two views of a saved connection (Session 10, B17). Configuration is the editor as
 * it always was; Runs is the connection's own history. `?tab=runs` opens the second, so a link
 * can land on it; a new connection has no history and no views to switch.
 *
 * A pressed-button group rather than `SkyTabList`, as on the run page (`runs/detail.tsx`) and for
 * the same reason (ux-s10 §1): in skypoint-ui 2.4.0 the tab triggers pointed `aria-controls` at a
 * panel only the library could render, so axe reported `aria-valid-attr-value` (critical) on
 * every screen that rendered its own panel. 2.17.0 fixes that
 * (`skypointUi.libraryContract.test.tsx`); moving to `SkyTabList` + `SkyTabPanel` is a backlog
 * row. Two buttons with `aria-pressed` say the same thing and pass.
 */
type ConnectionTab = "configuration" | "runs";

const CONNECTION_TAB_LABELS: Record<ConnectionTab, string> = {
  configuration: "Configuration",
  // Round 4 item 6: one label for a record's runs on both editors.
  runs: "Run history",
};

const DEFAULT_ENTITY_QUERY: EntitiesQuery = {
  search: "",
  selected: "all",
  entityType: "all",
  page: 1,
  pageSize: ENTITY_PAGE_SIZE,
};

/** The page notice's key for "Connection saved…", so the next save or an edit replaces it. */
const SAVED_NOTICE_KEY = "connection-saved";

/** Why Save is off, if it is: a live import first (#975), then a running Validate (#1243). */
function saveOffReason(
  importLive: boolean,
  isValidating: boolean,
  displayNameMissing = false,
): string | undefined {
  if (importLive) return SAVE_AFTER_IMPORT;
  if (isValidating) return NOT_WHILE_VALIDATING;
  // #1198: an empty Display name is refused by the server, so Save does not send it.
  if (displayNameMissing) return ENTER_DISPLAY_NAME;
  return undefined;
}

function saveDisabled(
  importLive: boolean,
  isValidating: boolean,
  displayNameMissing = false,
): { saveDisabledReason?: string } {
  const reason = saveOffReason(importLive, isValidating, displayNameMissing);
  return reason ? { saveDisabledReason: reason } : {};
}

/**
 * A saved connection's draft with every entry for a table a test did not list dropped, as the
 * tick itself would be once the save's test refreshes the table list (FR-44).
 */
function draftListedBy(
  draft: EntityDraft,
  listed: ReadonlySet<string>,
  handNamed = false,
): EntityDraft {
  // R3 (#2428): a hand-named connection's list is the person's, and its validator lists nothing,
  // so a test never drops an edit or a tick from its draft (a GA4 report's edit survives Connect).
  if (handNamed) return draft;
  return {
    ticks: Object.fromEntries(Object.entries(draft.ticks).filter(([name]) => listed.has(name))),
    settings: Object.fromEntries(
      Object.entries(draft.settings).filter(([name]) => listed.has(name)),
    ),
    added: draft.added,
    removed: draft.removed,
    ...(draft.editedFrom ? { editedFrom: draft.editedFrom } : {}),
  };
}

/** The connector's own words for its entities, as the notices name them. */
function entityLabelsOf(
  definition:
    | {
        entitySelection?: {
          entityLabelSingular?: string | null;
          entityLabelPlural?: string | null;
        } | null;
      }
    | null
    | undefined,
): { singular: string; plural: string } {
  return {
    singular: (definition?.entitySelection?.entityLabelSingular ?? "entity").toLowerCase(),
    plural: (definition?.entitySelection?.entityLabelPlural ?? "entities").toLowerCase(),
  };
}

/** #1098 review: ticks a save left out because the save's own test did not list their tables. */
function unlistedTicksSentence(
  count: number,
  labels: { singular: string; plural: string },
): string {
  const noun = count === 1 ? labels.singular : labels.plural;
  return `${count.toLocaleString()} ${noun} you changed ${count === 1 ? "is" : "are"} not in what these values found, so ${count === 1 ? "that change was" : "those changes were"} not saved. Choose from the list now shown.`;
}

/** The answer beside Validate after a pass on a saved connection (#1098). */
function unsavedPassText(
  unsaved: boolean,
  saveRetests: boolean,
  entityPluralLower: string,
): string {
  if (!unsaved) return "Validation passed.";
  return saveRetests ? unsavedTestPassed(entityPluralLower) : UNSAVED_TEST_PASSED_HELD;
}

/** #1098 review: the bulk button that saves is off when Save is, with Save's reason. */
function bulkOff(reason: string | undefined): { bulkDisabledReason?: string } {
  return reason ? { bulkDisabledReason: reason } : {};
}

/** How many tables of a draft a test did not list. */
function draftUnlisted(draft: EntityDraft, listed: ReadonlySet<string>, handNamed = false): number {
  if (handNamed) return 0;
  const names = new Set([...Object.keys(draft.ticks), ...Object.keys(draft.settings)]);
  return [...names].filter((name) => !listed.has(name)).length;
}
const SAVE_FAILED_TITLE = "We could not save this connection";
/** #1198: why Save is off on a form whose Display name is empty. */
const ENTER_DISPLAY_NAME = "Enter a Display name to save.";

/** No field left yet (#1198). */
const NO_TOUCHED_FIELDS: ReadonlySet<string> = new Set();
/** The Display name's key in the touched set; a connector field id never starts with "@". */
const DISPLAY_NAME_TOUCHED = "@displayName";
const VALIDATE_FAILED_TITLE = "We could not validate this connection";

function defaultValuesFor(definition: ConnectorDefinition): FormValues {
  const values: FormValues = {};
  for (const field of definition.fieldDefinitions) {
    if (field.controlType === "checkbox" || field.controlType === "toggle") {
      values[field.id] = field.defaultValue === "true";
    } else {
      values[field.id] = field.defaultValue ?? "";
    }
  }
  return values;
}

function defaultImageTag(definition: ConnectorDefinition): string {
  return (
    definition.versions.find((version) => version.isDefault)?.tag ??
    definition.versions[0]?.tag ??
    ""
  );
}

type NamedRow = Pick<EntityRow, "name" | "sourceSchema" | "sourceTable">;

/**
 * #994: whether a table's warehouse name carries a schema part. The load names a table from its
 * entity name (`targetTableName`, `entityTableSegment`), so the name decides: more than the source
 * table's own name (`dbo.customers` over `customers`), or, with no source table stored, a stored
 * schema or a `schema.table` name, as Your data reads it (`server/services/dataLocation.ts`).
 */
function nameHasSchemaPart(row: NamedRow): boolean {
  if (row.sourceTable?.trim()) {
    return entityTableSegment(row.name) !== entityTableSegment(row.sourceTable);
  }
  if (row.sourceSchema?.trim()) return true;
  return /^[^.]+\.[^.]/.test(row.name);
}

/**
 * #994: false when the connection's tables land as `<NAME>_<TABLE>`. A connector whose tables are
 * its own list (Mouseflow and the other APIs) or typed by hand never has a schema part. A source
 * that lists its tables decides by their names once any is known; before that the schema part
 * stays, as the SQL sources have one.
 */
function catalogueHasSchemas(
  catalogueSource: ConnectorDefinition["catalogueSource"],
  rows: readonly NamedRow[],
): boolean {
  if (catalogueSource === "declared" || catalogueSource === "hand_named") return false;
  return rows.length === 0 || rows.some(nameHasSchemaPart);
}

/**
 * Round 6 (owner 2026-10-01 23:25): the hint under the Name field, with an example spelled the
 * way this instance's destination stores it (`shared/warehouse/targetTableName.ts`): upper case
 * on Snowflake, lower case on Databricks, and the source's own spelling for `<Schema>_<Table>`
 * with Keep source case, the whole name lower case with Lower case (#2459). `<SCHEMA>_<TABLE>`
 * stands for each selected table's own name (a source without schemas has none).
 */
function connectionNameHint(
  connectionName: string,
  naming: Omit<TableNaming, "connectionName">,
  hasSchemas: boolean,
): string {
  const nameCase = effectiveTableNameCase(naming.destination, naming.tableNameCase);
  const source = nameCase === "source";
  // #2459: Lower case keeps the whole name, the connection's Name included, in lower case.
  const fold = (name: string): string => foldForCase(name, naming.destination, nameCase);
  const shown = fold(connectionName.trim() || "<name>");
  const placeholder = hasSchemas ? "<Schema>_<Table>" : "<Table>";
  // Keep source case shows the source's own spelling; every other case folds the placeholder.
  const rest = source ? placeholder : fold(placeholder.toLowerCase());
  return `Goes into every table name this connection creates, for example ${shown}_${rest}. Cannot change later.`;
}

/** No table's own name was refused. */
const NO_TARGET_NAME_ERRORS: ReadonlyMap<string, string> = new Map();

/** No ticked tables share a warehouse table. */
const NO_COLLISIONS: ReadonlyMap<string, EntityNameCollision> = new Map();

/**
 * The ticked rows that would share a warehouse table, by entity name, with one sentence per
 * shared table worded from the first of its rows, so both rows and the message bar say the same
 * thing once. A row that has loaded is named in the case its first load fixed (review r2).
 */
function tickedNameCollisions(
  ticked: readonly EntityRow[],
  naming: TableNaming,
): ReadonlyMap<string, EntityNameCollision> {
  const found = new Map<string, EntityNameCollision>();
  const sentences = new Map<string, string>();
  const tableCases = new Map<string, TableNameCase>();
  for (const row of ticked) if (row.tableNameCase) tableCases.set(row.name, row.tableNameCase);
  const overrides = new Map<string, string>();
  for (const row of ticked) if (row.targetName) overrides.set(row.name, row.targetName);
  const names = ticked.map((row) => row.name);
  for (const [name, collision] of findTargetNameCollisions(names, naming, tableCases, overrides)) {
    let sentence = sentences.get(collision.target);
    if (!sentence) {
      sentence = tableNameCollisionSentence({ entityName: name, ...collision });
      sentences.set(collision.target, sentence);
    }
    found.set(name, { otherEntityName: collision.otherEntityName, sentence });
  }
  return found;
}

function newForm(definition: ConnectorDefinition): EditorForm {
  return {
    displayName: "",
    name: "",
    nameEdited: false,
    description: "",
    imageTag: defaultImageTag(definition),
    refreshType: "full",
    // #1932: a field the environment pins starts filled in, read-only.
    values: withPinnedValues(defaultValuesFor(definition), pinnedFieldsOf(definition)),
    entities: [],
    draft: EMPTY_DRAFT,
  };
}

/** "v1.6.0 (Latest)"; a label that does not name the tag goes beside it: "1.0.0 · Stable". */
function versionText(version: ConnectorDefinition["versions"][number]): string {
  if (!version.label) return version.tag;
  return version.label.includes(version.tag) ? version.label : `${version.tag} · ${version.label}`;
}

/**
 * The Connector version dropdown's options (owner feedback, 2026-09-28: "The Connector version
 * dropdown shows blank"). The options are the connector's published versions, but a connection
 * may store a tag that is not one of them: the dev walkthrough and the chaos scripts save
 * `skydata-dev`, a locally built image, and a version can be removed from the catalog after a
 * connection pinned it. The dropdown then had no option for its value and drew blank. A stored tag
 * that is not published is listed first, and says so, so the control always shows what the
 * connection runs; choosing another version is still a normal edit.
 */
function versionOptions(
  definition: ConnectorDefinition,
  storedTag: string,
): Array<{ value: string; text: string }> {
  // #926: "Latest" first, then newest to oldest; the server orders them too, and this keeps
  // the dropdown right for any answer.
  const published = orderConnectorVersions(definition.versions).map((version) => ({
    value: version.tag,
    text: versionText(version),
  }));
  if (!storedTag || definition.versions.some((version) => version.tag === storedTag)) {
    return published;
  }
  return [{ value: storedTag, text: `${storedTag} (not a published version)` }, ...published];
}

/** The form values a saved connection holds, as stored. */
function storedValuesOf(definition: ConnectorDefinition, detail: DataflowDetail): FormValues {
  const byId = new Map(detail.fields.map((field) => [field.id, field]));
  const values: FormValues = {};
  for (const field of definition.fieldDefinitions) {
    const saved = byId.get(field.id);
    if (!saved) {
      values[field.id] =
        field.controlType === "checkbox" || field.controlType === "toggle"
          ? field.defaultValue === "true"
          : (field.defaultValue ?? "");
      continue;
    }
    values[field.id] = savedFieldValue(saved, field.controlType);
  }
  return values;
}

/**
 * The form of a saved connection. `pinned` (the default) puts the environment's pinned values in
 * (#1932); false keeps the stored ones, for the baseline a pinned change is measured against.
 */
function formFromDetail(
  definition: ConnectorDefinition,
  detail: DataflowDetail,
  pinned = true,
): EditorForm {
  const stored = storedValuesOf(definition, detail);
  const values = pinned ? withPinnedValues(stored, pinnedFieldsOf(definition)) : stored;

  return {
    displayName: detail.displayName,
    // A stub may carry no Name; the field then shows empty and the hint uses the Display name,
    // instead of the page failing on an undefined string.
    name: detail.name ?? "",
    nameEdited: false,
    description: detail.description ?? "",
    imageTag: detail.imageTag ?? defaultImageTag(definition),
    refreshType: detail.refreshType,
    values,
    // The server holds the catalogue; the detail's first page seeds the grid's cache.
    entities: [],
    draft: EMPTY_DRAFT,
  };
}

/** The first page the detail carried, as the entity query's initial cache entry. */
function firstPageOf(detail: DataflowDetail): EntityPage {
  return {
    items: detail.entities,
    page: 1,
    pageSize: ENTITY_PAGE_SIZE,
    total: detail.entityCount,
  };
}

/**
 * The validator's list, with the selections the user already made kept, in the order the saved
 * list will have (#1149: schema, then table, `compareEntities`), not the source's.
 */
function mergeValidatedEntities(existing: EntityRow[], listed: ValidatedEntity[]): EntityRow[] {
  const previous = new Map(existing.map((row) => [row.name, row]));
  return inEntityOrder(listed).map((entity) => {
    const before = previous.get(entity.name);
    return {
      name: entity.name,
      displayName: entity.displayName,
      sourceSchema: entity.sourceSchema,
      sourceTable: entity.sourceTable,
      isSelected: before?.isSelected ?? false,
      incrementalEnabled: before?.incrementalEnabled ?? false,
      watermarkColumn: before?.watermarkColumn ?? null,
      keyColumns: before?.keyColumns ?? [],
      ...(entity.entityType ? { entityType: entity.entityType } : {}),
    };
  });
}

function toEntityInputs(rows: EntityRow[]): EntitySelectionInput[] {
  return rows.map((row) => ({
    name: row.name,
    isSelected: row.isSelected,
    incrementalEnabled: row.incrementalEnabled,
    watermarkColumn: row.watermarkColumn,
    keyColumns: row.keyColumns,
    // Lead round: a name typed for a table of the new connection goes up with it.
    ...(row.targetName ? { targetName: row.targetName } : {}),
    // #2428: a GA4 report goes up with its definition, in the create's transaction.
    ...(row.definition ? { definition: definitionInput(row.definition) } : {}),
  }));
}

/**
 * Lead round: the server's refusal of a table's own name, by entity, for the row's input. A 409
 * `TABLE_NAME_TAKEN` names the table in its `entityName` detail; a 400 names the field
 * `entities.<name>.targetName` (a table that has landed keeps its name).
 */
function targetNameRefusals(error: unknown): Map<string, string> {
  const found = new Map<string, string>();
  if (!(error instanceof ApiClientError)) return found;
  if (error.code === "TABLE_NAME_TAKEN") {
    const entity = error.details.find((detail) => detail.field === "entityName")?.issue;
    if (entity) found.set(entity, error.message);
    return found;
  }
  for (const detail of error.details) {
    const match = /^entities\.(.+)\.targetName$/.exec(detail.field ?? "");
    if (match?.[1]) found.set(match[1], detail.issue);
  }
  return found;
}

/** Which connection the form was last seeded for: a new one by connector, a saved one by id. */
function seedKeyOf(
  definition: ConnectorDefinition | undefined,
  isNew: boolean,
  dataflowId: string | null,
): string | null {
  if (!definition) return null;
  return isNew ? `new:${definition.name}` : `edit:${dataflowId}`;
}

/**
 * #927: what a saved connection that is not Ready still lacks, for the saved notice, so the
 * person is told what to do rather than only that the setup is incomplete.
 */
function setupIncompleteText(saved: DataflowDetail, entityPluralLower: string): string {
  // Asked to validate on create, but the check could not run (the hub or the runner away), or,
  // PR #1350 review, it ran but not on the values as saved (`reason: "not_counted"`).
  if (saved.validationOnCreate?.status === "unavailable") {
    if (
      saved.validationOnCreate.message &&
      saved.validationOnCreate.code !== VALIDATION_UNAVAILABLE_CODE
    ) {
      return `setup incomplete: ${validationSentence(saved.validationOnCreate.message, [])} Validate it again to import.`;
    }
    return saved.validationOnCreate.reason === "not_counted"
      ? "setup incomplete: the check ran, but not on the values as saved. Validate it again to import."
      : "setup incomplete: the check could not run as it saved. Validate it again to import.";
  }
  const validation = saved.lastValidation;
  if (!validation) return "setup incomplete: not validated yet. Validate it to import.";
  if (validation.status !== "success") {
    // #1267: the server's sentence names the missing credential ("Setup incomplete. Enter the
    // password and validate the connection.").
    if (validation.errorCode === CREDENTIAL_MISSING_CODE) {
      return `${validation.message.charAt(0).toLowerCase()}${validation.message.slice(1)}`;
    }
    return validation.errorCode === TARGET_CHANGED_CODE
      ? "setup incomplete: not validated since the target changed. Validate it to import."
      : "setup incomplete: the last validation failed. Validate it again to import.";
  }
  if (saved.selectedCount === 0) {
    return `setup incomplete: no ${entityPluralLower} selected. Select at least one to import.`;
  }
  return "setup incomplete. Validate it to import.";
}

/**
 * PR #1312 review: the "A credential is missing" bar for a reader who cannot edit the connection,
 * with no instruction they cannot follow.
 */
const CREDENTIAL_MISSING_READ_ONLY_MESSAGE =
  "Setup incomplete. A credential is missing, so this connection does not import until someone who can edit it enters the credential and validates it.";

/** The save bar's status line. */
/** #1129: what a bulk selection left the setup as, for its notice; nothing for a disabled one. */
function bulkSetupSentence(saved: DataflowDetail, entityPluralLower: string): string {
  if (saved.state === "ready") return "Setup complete.";
  if (saved.state === "disabled") return "";
  const text = setupIncompleteText(saved, entityPluralLower);
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function saveStatusOf(
  isDirty: boolean,
  saved: boolean,
  validation: LastValidation | null,
  zone: string,
): string {
  if (isDirty) return "Unsaved changes";
  if (!saved) return "Not saved yet";
  if (!validation?.at) return "Saved. Last validated never.";
  // #1098: "Last validated" names only a passing test; a failed one says so, so its time never
  // reads like a success.
  if (validation.status !== "success") {
    if (validation.errorCode === CREDENTIAL_MISSING_CODE) {
      // PR #1312 review: true whether a save removed it or it was never entered.
      return "Saved. Not validated while a credential is missing.";
    }
    return validation.errorCode === TARGET_CHANGED_CODE
      ? "Saved. Not validated since the target changed."
      : `Saved. Last validation failed ${formatInstant(validation.at, zone, "short", { named: true })}.`;
  }
  return `Saved. Last validated ${formatInstant(validation.at, zone, "short", { named: true })}.`;
}

/** The ticked rows held here that would land in one warehouse table (see `nameCollisions`). */
function formNameCollisions(
  form: EditorForm,
  isNew: boolean,
  gridRows: EntityRow[],
  tableNaming: Omit<TableNaming, "connectionName">,
): ReadonlyMap<string, EntityNameCollision> {
  return tickedNameCollisions(
    (isNew ? form.entities : gridRows).filter((row) => row.isSelected),
    { ...tableNaming, connectionName: form.name.trim() || null },
  );
}

/** A selected row held here, or drafted on another page, whose typed warehouse name breaks the rule. */
function formTargetNameInvalid(
  form: EditorForm,
  isNew: boolean,
  gridRows: EntityRow[],
  connectionName: string | null,
): boolean {
  const held = isNew
    ? form.entities
    : [
        ...gridRows,
        // A drafted row on another page has no stored schema here: its name stands in.
        ...Object.entries(form.draft.settings).map(([name, drafted]) => ({
          ...drafted,
          name,
          sourceSchema: null,
        })),
        ...form.draft.added,
      ];
  return held.some((row) => row.isSelected && targetNameIssue(row, connectionName) !== null);
}

/** The first page the detail carried, for the default query of a saved connection. */
function seededFirstPage(
  detail: DataflowDetail | undefined,
  query: EntitiesQuery,
  fetchedAt: number,
): { page: EntityPage; fetchedAt: number } | undefined {
  return detail && isDefaultEntityQuery(query)
    ? { page: firstPageOf(detail), fetchedAt }
    : undefined;
}

/** The grid's opening query: no search, every row, the first page at the default size. */
function isDefaultEntityQuery(query: EntitiesQuery): boolean {
  return (
    query.search === "" &&
    query.selected === "all" &&
    query.entityType === "all" &&
    query.page === 1 &&
    query.pageSize === ENTITY_PAGE_SIZE
  );
}

/** The detail's half of the list's one status (owner feedback, 2026-09-28). */
function statusInputOf(
  state: DataflowSummary["state"],
  detail: DataflowDetail | undefined,
  listedRun: DataflowSummary["lastRun"] | undefined,
) {
  return {
    state,
    isProcessing: detail?.isProcessing ?? false,
    isFailed: detail?.isFailed ?? false,
    lastRunId: detail?.lastRunId ?? null,
    lastRefreshAt: detail?.lastRefreshAt ?? null,
    activeRunId: detail?.activeRunId ?? null,
    // Review r1 S2: the list row is a second query; it speaks for the detail's run only.
    // A row still about an older run would pair that run's outcome with the newer run's
    // flag, so it is dropped until the list catches up.
    lastRun: listedRun && listedRun.runId === detail?.lastRunId ? listedRun : undefined,
  };
}

function editorLoadFailure(failure: ApiClientError, onRetry: () => void) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      testId="connection-editor-error"
      title="We could not open this connection"
      message={failureMessage(failure, "We could not open this connection.")}
      actions={
        <SkyButton size="small" icon={RetryIcon} onClick={onRetry}>
          Try again
        </SkyButton>
      }
    />
  );
}

/** The tab in the URL; a new connection has only its configuration. */
function connectionTabOf(dataflowId: string | null, requested: string | null): ConnectionTab {
  return dataflowId !== null && requested === "runs" ? "runs" : "configuration";
}

/** Before the connection and its connector are both in hand: the failure, else the skeleton. */
function editorNotReady(failure: ApiClientError | null, onRetry: () => void) {
  return failure ? editorLoadFailure(failure, onRetry) : <EditorSkeleton />;
}

/** The field a Browse button was pressed for, while its dialog is open. */
function fieldById(definition: ConnectorDefinition, fieldId: string | null) {
  return fieldId ? definition.fieldDefinitions.find((field) => field.id === fieldId) : undefined;
}

/** A typed Name is checked as it is typed; a derived one always satisfies the rule. */
function typedNameInvalid(isNew: boolean, form: EditorForm): boolean {
  return isNew && form.nameEdited && !isConnectionName(form.name);
}

/**
 * The save area's classes. Read-only, only spacing for an error; otherwise pinned, and with an
 * error above it the bar's top margin is part of the pinned block, so it is painted rather than
 * see-through (review r1 nit).
 */
/**
 * #1244: the panel's own box. On a short screen (1024 x 640) a long refusal pinned with the bar
 * grew over the form and took the clicks meant for the Display name, so the panel holds at most a
 * quarter of the window and scrolls inside itself.
 */
const SAVE_ERROR_PANEL_CLASS = "max-h-[25vh] overflow-y-auto bg-background pt-2";

function saveAreaClass(readOnly: boolean, hasError: boolean): string | undefined {
  if (readOnly) return hasError ? "mt-4" : undefined;
  return hasError ? "sticky bottom-0 z-20 bg-background" : "sticky bottom-0 z-20";
}

/** Why Save waits, when it does: a name collision first, then a broken warehouse name. */
function saveBlocked(
  collides: boolean,
  targetNameInvalid: boolean,
  entityPluralLower: string,
  displayNameBroken = false,
  descriptionTooLong = false,
): { saveBlockedReason?: string } {
  // #1047: a Display name the server would refuse (its rule, or another connection's name) is
  // said at the field, and Save waits for it rather than sending a request known to fail.
  if (displayNameBroken) {
    return { saveBlockedReason: "Correct the Display name to save." };
  }
  // PR #1276 review: so is a Description over the server's limit.
  if (descriptionTooLong) {
    return { saveBlockedReason: "Shorten the Description to save." };
  }
  if (collides) {
    return {
      saveBlockedReason: `Two selected ${entityPluralLower} would land in one warehouse table. Select one of them to save.`,
    };
  }
  if (targetNameInvalid) {
    return {
      saveBlockedReason:
        "A warehouse table name breaks its rule. Correct it, or clear it to keep the derived name.",
    };
  }
  return {};
}

function EditorSkeleton() {
  return (
    <SkySkeleton testId="connection-editor-loading">
      <div className="grid gap-4 lg:grid-cols-2">
        {[0, 1].map((pane) => (
          <div key={pane} className="flex flex-col gap-3 rounded-lg border border-border p-4">
            <SkySkeletonItem width={160} />
            {[0, 1, 2, 3].map((row) => (
              <SkySkeletonItem key={row} size={32} />
            ))}
          </div>
        ))}
      </div>
    </SkySkeleton>
  );
}

/**
 * The definition and key to seed the form from, once both have loaded and this key has not been
 * seeded yet (an edit also waits for its detail row); null otherwise.
 */
function pendingSeed({
  definition,
  seedKey,
  seededFor,
  isNew,
  detail,
}: {
  definition: ConnectorDefinition | undefined;
  seedKey: string | null;
  seededFor: string | null;
  isNew: boolean;
  detail: DataflowDetail | undefined;
}): { loaded: ConnectorDefinition; key: string } | null {
  if (definition && seedKey && seededFor !== seedKey && (isNew || detail)) {
    return { loaded: definition, key: seedKey };
  }
  return null;
}

/** The schema change log drawer: one table's (or every table's, `null`), and its first filter. */
type SchemaDrawerState = {
  entityName: string | null;
  initialStatus?: "open" | "all";
  /** #1663: the run whose page linked here; the log opens on that import's changes. */
  runId?: string;
} | null;

/** A run's "Schema changed at source" link opens the log on its table with every change listed. */
function linkedSchemaDrawer(
  linkedSchemaTable: string | null,
  linkedRunId: string | null,
): SchemaDrawerState {
  if (!linkedSchemaTable) return null;
  return {
    entityName: linkedSchemaTable,
    initialStatus: "all",
    ...(linkedRunId ? { runId: linkedRunId } : {}),
  };
}

/**
 * T-51: a new connection of a connector the tenant does not offer for new connections. The API
 * would refuse the save with the same sentence; say it up front. Null when it is offered.
 */
function notOfferedForNew(
  isNew: boolean,
  definition: ConnectorDefinition,
  navigate: (to: string) => void,
): ReactNode {
  if (!isNew || definition.offer === undefined || definition.offer === "offered") return null;
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      testId="connector-not-allowed"
      title={`${definition.displayName} is not offered for new connections`}
      message={
        definition.offer === "not_selected"
          ? CONNECTOR_NOT_ALLOWED_MESSAGE
          : CONNECTOR_NOT_AVAILABLE_MESSAGE
      }
      actions={
        <SkyButton size="small" onClick={() => navigate(absolutePath(NEW_CONNECTION_PATH))}>
          Choose another connector
        </SkyButton>
      }
    />
  );
}

/** One message card of the editor's validation notes (#2439: split out of the render for clarity). */
function validationNoteCard(
  intent: "warning" | "error",
  testId: string,
  title: string,
  message: ReactNode,
  className?: string,
) {
  return (
    <div className="mb-4">
      <SkyMessageBar
        intent={intent}
        layout="multiline"
        testId={testId}
        className={className}
        title={title}
        message={message}
      />
    </div>
  );
}

type StoredFailure = { at: string; message: string; errorCode?: string | null };

/** The warning cards: unsaved target change, target changed, credential missing. */
function stateNoteCards(
  failed: StoredFailure | null,
  unsavedTargetChanges: readonly string[],
  readOnly: boolean,
) {
  const missingMessage = readOnly ? CREDENTIAL_MISSING_READ_ONLY_MESSAGE : (failed?.message ?? "");
  return (
    <>
      {unsavedTargetChanges.length > 0
        ? validationNoteCard(
            "warning",
            "target-change-needs-test",
            "A connection test will be needed",
            `You changed ${joinFieldNames(unsavedTargetChanges)}. After you save, this connection does not run until a connection test passes. The stored credentials are kept, but skyData does not use them against the new target until the test passes.`,
          )
        : null}
      {failed?.errorCode === TARGET_CHANGED_CODE
        ? validationNoteCard(
            "warning",
            "validation-target-changed",
            "Test the connection",
            failed.message,
          )
        : null}
      {failed?.errorCode === CREDENTIAL_MISSING_CODE
        ? validationNoteCard(
            "warning",
            "validation-credential-missing",
            "A credential is missing",
            // PR #1312 review: a Viewer cannot enter it or validate, so is told the state only.
            missingMessage,
          )
        : null}
    </>
  );
}

/**
 * The red cards (#2439). One red card, never two: a stored failed test that a no-verdict answer
 * left in place is folded into the no-verdict card as its last line. The no-verdict title is not
 * "Validation failed" (SD2), and its sentence is worded by cause on the server.
 */
function failureNoteCards(input: {
  storedFailed: StoredFailure | null;
  noVerdictShown: {
    message: string;
    at: string;
    values: Record<string, unknown>;
    errorCode?: string | null;
    referenceId?: string;
  } | null;
  /** #2485: the connector the card is for; only SQL Server's reach answers carry details. */
  connectorName: string;
  fields: Parameters<typeof validationSentence>[1];
  zone: Parameters<typeof validatedAtSentence>[1];
  readOnly: boolean;
}) {
  const { storedFailed, noVerdictShown, connectorName, fields, zone, readOnly } = input;
  const lastFailed = storedFailed
    ? [
        validatedAtSentence(storedFailed.at, zone, "Last validation failed"),
        validationSentence(storedFailed.message, fields),
      ]
    : [];
  if (noVerdictShown) {
    return validationNoteCard(
      "error",
      "validation-no-verdict",
      NO_VERDICT_TITLE,
      <>
        {joinParts(validationSentence(noVerdictShown.message, fields), ...lastFailed)}
        {isReachFailure(connectorName, noVerdictShown.errorCode) ? (
          <ReachDetails
            values={noVerdictShown.values}
            reference={noVerdictShown.referenceId ?? null}
            at={noVerdictShown.at}
            zone={zone}
            testId="validation-no-verdict-details"
          />
        ) : null}
      </>,
      VALIDATION_FAILED_CARD,
    );
  }
  if (!storedFailed) return null;
  const saveHint = readOnly
    ? ""
    : "You can still save this connection. Its setup stays incomplete until validation succeeds.";
  return validationNoteCard(
    "error",
    "validation-failed",
    "Validation failed",
    joinParts(...lastFailed, saveHint),
    // #952 review: the library's error bar is a pale tint; a validation answer is read at a
    // glance, so its border is full strength and its title in the error colour.
    VALIDATION_FAILED_CARD,
  );
}

export default function ConnectionEditorPage() {
  const zone = useDisplayTz();
  // #1080: the header's overflow menu says the destination's reason, as Import now does.
  const destinationRunReason = destinationBlockReason(useDestinationReadiness());
  // The saved notice is built after the save's round trip, so it reads the reason as it is then,
  // not as it was when Save was clicked (#1080 review: the notice's Import now follows the header).
  const destinationRunReasonRef = useRef(destinationRunReason);
  useEffect(() => {
    destinationRunReasonRef.current = destinationRunReason;
  }, [destinationRunReason]);
  const params = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const [, navigate] = useLocation();
  const notice = useNotice();
  // Only for a failure whose control is not the form (the header's Import now, the notice's own
  // action): it lands in the notice, directly under the header that holds the button.
  const showApiError = useApiErrorNotice();
  // A failed save or validation is said just above the save bar, where Save and Validate are
  // (ux-s10 §3 rule 2), never in the notice behind the user's back.
  const formError = useInlineError();
  const [formErrorTitle, setFormErrorTitle] = useState(SAVE_FAILED_TITLE);
  const { can } = usePermissions();

  const routeId = params.id;
  const isNew = !routeId || routeId === "new";
  // #1652: an id that is not a uuid is never asked for; the page reads not found.
  const idMalformed = !isNew && routeId !== undefined && isMalformedRecordId(routeId);
  const dataflowId = isNew || idMalformed ? null : routeId;

  const canWrite = can("dataflows.write");
  const canValidate = can("dataflows.validate");
  const readOnly = !canWrite;

  // B-77 (round 9): a new connection is opened from a connector card, often by keyboard. Focus
  // goes to the form's first field once it is on screen, so the next Tab continues in the form
  // instead of starting again at the top of the shell.
  const firstField = useRef<HTMLInputElement>(null);
  const focusedFirstField = useRef(false);
  useEffect(() => {
    if (!isNew || focusedFirstField.current || !firstField.current) return;
    focusedFirstField.current = true;
    firstField.current.focus();
  });

  // Round 6 (owner 2026-10-01 23:10 and 23:40): how this instance's destination spells a table,
  // for the Name hint and the picker's duplicate check. With no binding (none yet, or one that
  // did not load) it is Snowflake's native case, which is what the one loader writes.
  // #1106: from the destination summary, which every reader of connections may ask; the binding
  // GET is Instance admin and above.
  const summaryQuery = useDestinationSummary();
  const tableNaming = useMemo<Omit<TableNaming, "connectionName">>(
    () => ({
      destination: summaryQuery.data?.type ?? "snowflake",
      tableNameCase: summaryQuery.data?.tableNameCase ?? "native",
      caseFixed: summaryQuery.data?.tableNameCaseFixed === true,
    }),
    [summaryQuery.data],
  );

  const tab = connectionTabOf(dataflowId, searchParams.get("tab"));
  // Replace, not a new history entry: a tab is not a page the back button should step through.
  // The form's state lives on this page, so an unsaved edit survives a look at the Runs tab.
  const selectTab = (next: string) => {
    if (!dataflowId || next === tab) return;
    navigate(absolutePath(`${connectionPath(dataflowId)}${next === "runs" ? "?tab=runs" : ""}`), {
      replace: true,
    });
  };

  const detailQuery = useDataflow(dataflowId);
  const connectorName = isNew
    ? searchParams.get("connector")
    : (detailQuery.data?.connectorName ?? null);
  const definitionQuery = useConnectorDefinition(connectorName);

  const [form, setForm] = useState<EditorForm | null>(null);
  const [baseline, setBaseline] = useState<EditorForm | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  /** Whether the posted "saved" notice carries Import now (#1284). */
  const [savedNoticeHasImport, setSavedNoticeHasImport] = useState(false);
  // #1198 (PR #1276 review): the fields a person has left, which say their rule (a required field
  // left empty) at once, before any Save or Validate. Ids of the connector's fields, and
  // `DISPLAY_NAME_TOUCHED` for the Display name.
  const [touchedFields, setTouchedFields] = useState<ReadonlySet<string>>(NO_TOUCHED_FIELDS);
  const touchField = useCallback((fieldId: string) => {
    setTouchedFields((previous) =>
      previous.has(fieldId) ? previous : new Set([...previous, fieldId]),
    );
  }, []);
  // #963: true only after a save the server accepted, until the next press of Save. The note
  // under the form says "saved" only then; a refused save leaves the red banner to speak.
  const [savedOk, setSavedOk] = useState(false);
  const [validation, setValidation] = useState<LastValidation | null>(null);
  // Review of round 8: the form values the last passing re-validation on this screen sent. The
  // server keeps that test when the save sends the same target, so the "a connection test will
  // be needed" warning compares with these, not only with the saved values.
  /**
   * #927: the last Validate on this screen, with the form values it sent. Its answer is said
   * beside the button until the next save; a passing one whose values are unchanged makes a
   * create ask the server to validate on create, so it saves Ready in one pass.
   */
  const [lastValidate, setLastValidate] = useState<{
    at: string;
    status: "success" | "failed";
    message: string;
    values: Record<string, unknown>;
    /**
     * #1098: a saved connection's test of values it does not hold. The server recorded nothing,
     * so the saved state, Import now and "Last validated" are as they were, and a Save of these
     * values asks the server to test them again (`validate` on the update).
     */
    unsaved: boolean;
    /** SD2 (#1465): the source answered nothing about the connection, and nothing was changed. */
    noVerdict?: boolean;
    /** #2485: the hub's code and the answer's reference id, for the "could not reach" card. */
    errorCode?: string | null;
    referenceId?: string;
    /**
     * #1098 review: the tables an unsaved pass found. The table list on screen is still the
     * saved connection's, so a Save that tests these values keeps only the ticks for tables in
     * this list; the save's own test then refreshes the list.
     */
    listed: ReadonlySet<string> | null;
    /** SD3: the selected tables a recorded pass removed because the source no longer lists them. */
    removedSelected?: { count: number; names: string[] };
  } | null>(null);
  // #927 follow-up (owner): which Validate the last answer was asked from, and where to take the
  // reader when the save bar's one answers: the table list after a pass, the first invalid field
  // after a failure. Run after the save bar's own effect, so this focus is the one that stays.
  const validateSource = useRef<"bar" | "inline">("inline");
  const entitiesHeading = useRef<HTMLElement>(null);
  const formSection = useRef<HTMLElement>(null);
  const lastValidateKey = lastValidate ? `${lastValidate.at}:${lastValidate.status}` : null;
  const lastValidateStatus = lastValidate?.status ?? null;
  useEffect(() => {
    if (lastValidateKey === null || validateSource.current !== "bar") return;
    validateSource.current = "inline";
    if (lastValidateStatus === "success") {
      reveal(entitiesHeading.current);
      return;
    }
    // Nothing invalid on the form: the answer beside the button keeps the focus.
    revealField(formSection.current?.querySelector<HTMLElement>('[aria-invalid="true"]') ?? null);
  }, [lastValidateKey, lastValidateStatus]);
  /**
   * The state the server reported for the most recent write.
   *
   * `state` is the server's to derive (FR-42), and the cached detail row is
   * refetched in the background after a write — so between the write answering
   * and the refetch landing, the cache still holds the state from BEFORE the
   * save. Reading the write's own answer means the badge changes when the
   * change happens, rather than one round trip later.
   */
  const [stateFromLastWrite, setStateFromLastWrite] = useState<DataflowSummary["state"] | null>(
    null,
  );
  const [addEntityOpen, setAddEntityOpen] = useState(false);
  // Product gaps row 2: one table run on its own from its row menu (re-sync, or since a date).
  const tableRunsOn = useTableRunsEnabled();
  const [tableRun, setTableRun] = useState<{ mode: "full" | "since"; table: string } | null>(null);
  const tableActions = useMemo(
    () => ({
      onResync: (table: string) => setTableRun({ mode: "full", table }),
      onReadSince: (table: string) => setTableRun({ mode: "since", table }),
    }),
    [],
  );
  // #1077: a connector that cannot import incrementally is offered Re-sync only, never a since date.
  const resyncOnlyActions = useMemo(() => ({ onResync: tableActions.onResync }), [tableActions]);
  // Product gaps row 7: the open schema changes of this connection's tables, and the change log
  // drawer (one table's, or every table's from the message bar).
  const schemaSummary = useSchemaChangeSummary(!isNew);
  const schemaRows = useMemo(
    () => (schemaSummary.data ?? []).filter((row) => row.dataflowId === dataflowId),
    [schemaSummary.data, dataflowId],
  );
  const schemaCounts = useMemo(
    () => new Map(schemaRows.map((row) => [row.entityName, row])),
    [schemaRows],
  );
  const schemaDecisionsNeeded = decisionNeededSentence(schemaRows);
  // Round 6 (T-02): a run's "Schema changed at source" line links here with the table in
  // `?schemaChanges=`, and the log opens on that table with every change listed.
  const linkedSchemaTable = isNew ? null : searchParams.get("schemaChanges");
  const [schemaDrawer, setSchemaDrawer] = useState<SchemaDrawerState>(
    linkedSchemaDrawer(linkedSchemaTable, isNew ? null : searchParams.get("run")),
  );
  const openSchemaChanges = useCallback(
    (entityName: string) => setSchemaDrawer({ entityName }),
    [],
  );
  // Closing the log a run's link opened drops `?schemaChanges=` (replace, keeping the tab), so a
  // reload does not open it again.
  const closeSchemaChanges = () => {
    setSchemaDrawer(null);
    if (!dataflowId || !searchParams.has("schemaChanges")) return;
    navigate(absolutePath(`${connectionPath(dataflowId)}${tab === "runs" ? "?tab=runs" : ""}`), {
      replace: true,
    });
  };
  const [browseFieldId, setBrowseFieldId] = useState<string | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  /**
   * G3: the `updatedAt` this screen last saw, sent on every write. Moved by every
   * answer the server gives, including a re-validation and a "select all matching",
   * because each of those moves the row's stamp too.
   */
  const [loadedUpdatedAt, setLoadedUpdatedAt] = useState<string | null>(null);
  /** The server's counts as of the last answer, so the badge is right before a refetch lands. */
  const [serverCounts, setServerCounts] = useState({
    entityCount: 0,
    selectedCount: 0,
    viewCount: 0,
    selectedViewCount: 0,
  });
  const [staleConflict, setStaleConflict] = useState(false);
  /** Lead round: the last save's refusal of a table's own name, by entity, until it is retyped. */
  const [targetNameErrors, setTargetNameErrors] =
    useState<ReadonlyMap<string, string>>(NO_TARGET_NAME_ERRORS);
  /** #1047: the last refusal's reason for the Display name or the Name, until it is retyped. */
  const [refusedFields, setRefusedFields] = useState<RefusalFieldErrors>(NO_REFUSAL_FIELD_ERRORS);
  /** #1099: the panel's lines under the sentence, fields named by their labels on screen. */
  const [panelLines, setPanelLines] = useState<string[]>([]);
  const [entityQuery, setEntityQuery] = useState<EntitiesQuery>(DEFAULT_ENTITY_QUERY);

  const definition = definitionQuery.data;
  const detail = detailQuery.data;
  // R3 (#2428): a hand-named connection's list is the person's; a test never filters its draft.
  const handNamed = definition?.catalogueSource === "hand_named";
  // #2428: a GA4 connection's entities are reports with a definition: the Reports list and drawer.
  const reportKind = definition?.entitySelection?.definitionKind === "ga4_report";
  const [reportDrawer, setReportDrawer] = useState<{
    mode: ReportDrawerMode;
    /** The report Edit, View or Duplicate starts from; null for a new one. */
    from: string | null;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RemoveReportTarget | null>(null);
  const [rebuildLines, setRebuildLines] = useState<string[] | null>(null);

  // 2026-09-28 cred-check: the daily credential check's result for this connection, and the
  // dates typed for its expiring credentials. Read only for a saved connection.
  const credentials = useCredentials(!isNew);
  const credentialCheck = dataflowId
    ? credentialTarget(credentials.data, "dataflow", dataflowId)
    : null;
  const setCredentialExpiry = useSetDataflowExpiry(dataflowId ?? "");

  // Session 10 (P12): the connection's import, live, for the header and the panel alike. The
  // run is the newest of this tab's start, the list's row and the detail's `lastRunId` (S4);
  // the audited detail is never re-read to find it.
  const canStartImport = can("runs.start");
  const canCancelImport = can("runs.cancel");
  // #1202 (finding 107): the run page names the extract after the connection ("Extract from
  // ueg_customers"), so this header does too, the connector's name only before a name is saved.
  const connectorWord = detail?.displayName ?? definition?.displayName ?? null;
  // #1236: the load step's name is the run page's, "Load into Snowflake", from the instance's
  // destination type (the unaudited destination summary). A load cannot run with no destination
  // (#923), so the type is the one the step loads into.
  const importDestination = summaryQuery.data?.type ?? null;
  // #2389: the running extract's liveness line names the connector ("Still reading from Mouseflow.").
  const sourceName = definition?.displayName ?? null;
  const importVocabulary = useMemo(
    () => ({
      connector: connectorWord,
      destination: importDestination,
      ...(sourceName && dataflowId ? { sourceNames: new Map([[dataflowId, sourceName]]) } : {}),
    }),
    [connectorWord, importDestination, sourceName, dataflowId],
  );
  const importActivity = useConnectionImport({
    dataflowId,
    detailRunId: detail?.lastRunId ?? null,
    vocabulary: importVocabulary,
  });
  const { startImport, isPending: importStarting } = useStartImport();
  // The list row's `lastRun` carries this connection's own outcome in that run (post-release
  // 607e63a); the detail does not. Same query as `useConnectionImport`'s, so no extra read.
  // #1047: every connection, a new one's page too, so a Display name another connection holds is
  // said at the field before the save.
  const listedConnections = useDataflows(true).data;
  const listedConnection = listedConnections?.find((row) => row.id === dataflowId);
  const listedRun = listedConnection?.lastRun;
  // #1650: every Import now on this page (header, saved notice, empty state) posts its refusal
  // under one key per connection, so the next start replaces it, and it goes once the import is
  // live or another run has come and gone. A start answers with the usual "Import started" notice
  // (under its own key, so the refusal's dismissal never takes it away). A press that finds one
  // already in flight is dropped by the hook.
  const importIsLive = isActivityLive(importActivity);
  const importRefusals = useImportStartRefusals(
    useMemo(
      () => [{ id: dataflowId ?? "new", runId: listedRun?.runId ?? null, live: importIsLive }],
      [dataflowId, listedRun?.runId, importIsLive],
    ),
  );
  const startImportHere = (id: string) =>
    startImport(id, {
      onSuccess: (result) => {
        notice.dismiss(importStartNoticeKey(id));
        notice.success(`Import started for ${detail?.displayName || "this connection"}.`, {
          key: `import-started:${id}`,
          action: {
            label: "View import",
            onClick: () => navigate(absolutePath(`/runs/${result.runId}`)),
          },
        });
      },
      onError: (error) => {
        importRefusals.remember(id, listedRun?.runId ?? null);
        showApiError(error, "We could not start the import.", { key: importStartNoticeKey(id) });
      },
    });
  // Round 6, S5: Run now's options for this connection ("Import with options").
  const [importOptionsOpen, setImportOptionsOpen] = useState(false);
  // #977: the confirm for Cancel run.
  const [cancelTarget, setCancelTarget] = useState<CancelImportTarget | null>(null);
  // #975: the import is queued or running, so Disable and Save wait for it. #977: Cancel run shows.
  const importLive = !isNew && isActivityLive(importActivity);

  const seed = useCallback((next: EditorForm) => {
    setForm(next);
    setBaseline(next);
    setShowErrors(false);
    setTouchedFields(NO_TOUCHED_FIELDS);
    setLastValidate(null);
  }, []);

  /** Everything a server answer tells this screen about the row it is editing. */
  const takeServerAnswer = useCallback((saved: DataflowDetail) => {
    setStateFromLastWrite(saved.state);
    setLoadedUpdatedAt(saved.updatedAt);
    setServerCounts({
      entityCount: saved.entityCount,
      selectedCount: saved.selectedCount,
      viewCount: saved.viewCount ?? 0,
      selectedViewCount: saved.selectedViewCount ?? 0,
    });
    setStaleConflict(false);
  }, []);

  // 2026-09-28: Disable and Enable in the header. The answer is this screen's latest write: its
  // state and its stamp move at once, so the next Save does not read as someone else's (G3).
  const toggle = useConnectionToggle(takeServerAnswer);
  // Alert routing: the saved notice of a create offers Watch (owner 2026-09-28).
  const watchNew = useSetWatch();

  // Seeded ONCE per connection, not on every refetch: a background invalidation
  // that reset the form would throw away a password the user had typed but not
  // yet saved. A save and a discard reseed explicitly.
  //
  // Derived during render rather than in an effect. An effect would paint one
  // frame of an empty form over a connection that is already loaded, and the
  // React compiler's lint rules call a synchronous `setState` in an effect a
  // cascading render — correctly, because this one has nothing to synchronise
  // with: the data is already here.
  const seedKey = seedKeyOf(definition, isNew, dataflowId);
  const seedFromLoaded = (loaded: ConnectorDefinition, key: string) => {
    setSeededFor(key);
    const loadedForm = isNew ? newForm(loaded) : formFromDetail(loaded, detail as DataflowDetail);
    seed(loadedForm);
    // #1932: a saved connection that holds another address than the pinned one has a change to
    // save, so the baseline is what is stored and Save offers it.
    const pins = pinnedFieldsOf(loaded);
    if (!isNew && !holdsPinnedValues(storedValuesOf(loaded, detail as DataflowDetail), pins)) {
      setBaseline(formFromDetail(loaded, detail as DataflowDetail, false));
    }
    setStateFromLastWrite(null);
    // The connector may declare its own page size; the query carries it from the start so
    // the page fetched and the page the grid paginates are always the same length.
    setEntityQuery({
      ...DEFAULT_ENTITY_QUERY,
      pageSize: loaded.entitySelection?.pageSize ?? ENTITY_PAGE_SIZE,
    });
    setStaleConflict(false);
    if (!isNew && detail) {
      setValidation(detail.lastValidation);
      setLoadedUpdatedAt(detail.updatedAt);
      setServerCounts({
        entityCount: detail.entityCount,
        selectedCount: detail.selectedCount,
        viewCount: detail.viewCount ?? 0,
        selectedViewCount: detail.selectedViewCount ?? 0,
      });
    } else {
      setValidation(null);
    }
  };
  const toSeed = pendingSeed({ definition, seedKey, seededFor, isNew, detail });
  if (toSeed) seedFromLoaded(toSeed.loaded, toSeed.key);

  const fieldDefinitions = useMemo(
    () => definition?.fieldDefinitions ?? [],
    [definition?.fieldDefinitions],
  );
  const values = useMemo(() => form?.values ?? {}, [form?.values]);
  const fieldState = useFieldDependencies(fieldDefinitions, values);
  const fieldErrors = useValidation(fieldDefinitions, values, fieldState);

  const createDataflow = useCreateDataflow();
  const updateDataflow = useUpdateDataflow(dataflowId ?? "");
  const patchEntities = usePatchEntities(dataflowId ?? "");
  const invalidateDataflows = useInvalidateDataflows();
  const validateUnsaved = useValidateDataflow();
  const revalidate = useRevalidateDataflow(dataflowId ?? "");

  const isSaving = createDataflow.isPending || updateDataflow.isPending || patchEntities.isPending;
  const isValidating = validateUnsaved.isPending || revalidate.isPending;

  // The page the grid shows. A saved connection's comes from the server, seeded with the
  // first page the detail carried; the default query therefore costs no second request.
  const isDefaultQuery = isDefaultEntityQuery(entityQuery);
  const entityPage = useDataflowEntities(
    dataflowId,
    entityQuery,
    seededFirstPage(detail, entityQuery, detailQuery.dataUpdatedAt),
  );

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline],
  );

  // "Saved" is never shown over unsaved edits: the confirmation goes once the form is dirty.
  useDismissNoticeWhen(SAVED_NOTICE_KEY, isDirty);

  // #1284: the notice's Import now is offered at save time. Once an import of this connection is
  // live (from the header, the Runs tab or the list) or the destination stops being Ready, the
  // click would only meet the server's refusal, so the notice goes, as the header's button is off.
  // Only a notice that carries Import now goes: the confirmation, the setup guidance and Watch stay.
  useDismissNoticeWhen(
    SAVED_NOTICE_KEY,
    savedNoticeHasImport && (isActivityLive(importActivity) || destinationRunReason !== null),
  );

  // #927: whether the form changed since the last Validate on this screen, and so whether a
  // passing one still speaks for these values.
  const changedSinceValidate = useMemo(
    () =>
      lastValidate !== null &&
      form !== null &&
      JSON.stringify(form.values) !== JSON.stringify(lastValidate.values),
    [form, lastValidate],
  );
  // A Start date the form refuses keeps the save a Draft: the hub's pass says nothing of a Start date
  // later than today, and a Ready connection must not carry the value the field refuses (#1094).
  const futureDayRefused = fieldDefinitions.some(
    (def) =>
      notAfterTodayIssue(
        def.validation?.notAfterToday,
        String(values[def.id] ?? ""),
        new Date(),
        zone,
      ) !== null,
  );
  // #1094 review: Save holds only for a Start date changed on this screen. A saved value that is
  // today in the zone of whoever saved it (and so later than today here) is still shown at the
  // field, but the save goes: the server, which accepts any day that is today somewhere, decides.
  const futureDayHeld = fieldDefinitions.some(
    (def) =>
      (isNew ||
        JSON.stringify(values[def.id] ?? "") !== JSON.stringify(baseline?.values[def.id] ?? "")) &&
      notAfterTodayIssue(
        def.validation?.notAfterToday,
        String(values[def.id] ?? ""),
        new Date(),
        zone,
      ) !== null,
  );
  const validatedUnchanged =
    isNew &&
    canValidate &&
    lastValidate?.status === "success" &&
    !changedSinceValidate &&
    !futureDayRefused;
  // #1098: a saved connection's test of unsaved values recorded nothing, so a Save of exactly the
  // values it tested asks the server to test them again (`validate` on the update), pass or fail:
  // the server's own answer decides the state, so values that just failed never save as Ready.
  // A pass with a Start date the form refuses is not sent (#1094: it would make Ready a value
  // the field refuses); a failure always is.
  const saveRetests =
    !isNew &&
    lastValidate?.unsaved === true &&
    canValidate &&
    !changedSinceValidate &&
    (lastValidate.status === "failed" || !futureDayRefused);

  /**
   * "Connection saved. {name}: setup complete." under the page title (ux-s10 §3), with Import now
   * when the connection can import and the user may start one. It names the setup, as the save
   * bar does, never a status word: re-saving an Active or Failed connection must not say "Ready"
   * under a header that says otherwise (review r2 S1'). On create it is posted for the new
   * connection's page (`forPath`) just before the navigation, so it survives that one change.
   */
  const confirmSaved = (saved: DataflowDetail, forPath?: string) => {
    const name = saved.displayName;
    const ready = saved.state === "ready";
    const importAction =
      ready &&
      canStartImport &&
      !isActivityLive(importActivity) &&
      destinationRunReasonRef.current === null
        ? {
            label: "Import now",
            onClick: (noticeId: string) => {
              notice.dismiss(noticeId);
              startImportHere(saved.id);
            },
          }
        : undefined;
    // Alert routing (owner 2026-09-28): creating a connection does not watch it; the notice of a
    // create offers Watch. Since #927 a create can be Ready (validate on create); its notice still
    // offers Watch, the header's Import now is there for the import, and the notice's one action
    // is never contested.
    const watchAction =
      forPath !== undefined
        ? {
            label: "Watch",
            onClick: (noticeId: string) => {
              notice.dismiss(noticeId);
              watchNew.mutate(
                { targetType: "dataflow", targetId: saved.id, outcomes: WATCH_DEFAULT_OUTCOMES },
                { onError: (error) => showApiError(error, `We could not watch ${name}.`) },
              );
            },
          }
        : undefined;
    // #1098 review: a save that asked for its own test which could not run says so, a Ready one
    // too: the values it saved are not the ones the last test passed.
    const untested = saved.validationOnCreate?.status === "unavailable";
    setSavedNoticeHasImport(watchAction === undefined && importAction !== undefined);
    notice[ready && untested ? "warning" : "success"](
      ready
        ? untested
          ? saved.validationOnCreate?.message &&
            saved.validationOnCreate.code !== VALIDATION_UNAVAILABLE_CODE
            ? `Connection saved. ${name}: ${validationSentence(saved.validationOnCreate.message, definition?.fieldDefinitions ?? [])} These values are not tested yet. Validate it again to test them.`
            : saved.validationOnCreate?.reason === "not_counted"
              ? `Connection saved. ${name}: the check ran, but not on the values as saved, so these values are not tested yet. Validate it again to test them.`
              : `Connection saved. ${name}: the check could not run as it saved, so these values are not tested yet. Validate it again to test them.`
          : `Connection saved. ${name}: setup complete.`
        : `Connection saved. ${name}: ${setupIncompleteText(
            saved,
            (definition?.entitySelection?.entityLabelPlural ?? "entities").toLowerCase(),
          )}`,
      {
        key: SAVED_NOTICE_KEY,
        forPath,
        // Not offered while an import is live; the header's Import now is disabled then too.
        action: watchAction ?? importAction,
      },
    );
  };

  const setValue = useCallback((fieldId: string, value: unknown) => {
    setForm((previous) =>
      previous ? { ...previous, values: { ...previous.values, [fieldId]: value } } : previous,
    );
  }, []);

  /** Every field, so the server's field-set replacement keeps what it holds. */
  const fieldInputs = useCallback((): DataflowFieldInput[] => {
    if (!definition || !form) return [];
    return definition.fieldDefinitions
      .filter((field) => !field.isPlatformManaged)
      .map((field) => ({ id: field.id, value: encodeFieldValue(form.values[field.id]) }));
  }, [definition, form]);

  /** FR-43: only what the user actually retyped goes to a re-validation. */
  const changedFieldInputs = useCallback((): DataflowFieldInput[] => {
    if (!definition || !form || !baseline) return [];
    return definition.fieldDefinitions
      .filter((field) => !field.isPlatformManaged)
      .filter((field) => {
        // "Did the user retype this" is answered by the baseline this form was
        // seeded from, never by comparing the value to `MASKED_VALUE`. A stored
        // credential is seeded AS the sentinel, so an untouched one compares
        // equal here and is left out for the same reason as any other unchanged
        // field — while a field with nothing stored is seeded empty, so a user
        // whose credential really is five asterisks types a change and gets one.
        // Reading the sentinel itself would have dropped that value silently.
        const current = form.values[field.id];
        return JSON.stringify(current) !== JSON.stringify(baseline.values[field.id]);
      })
      .map((field) => ({ id: field.id, value: encodeFieldValue(form.values[field.id]) }));
  }, [definition, form, baseline]);

  const applyValidation = (
    status: "success" | "failed",
    message: string,
    errorCode: string | null,
    at: string,
    entities: ValidatedEntity[],
  ) => {
    setValidation({ status, message, at, by: null, errorCode });
    if (status !== "success") return;
    if (isNew) {
      setForm((previous) =>
        previous
          ? { ...previous, entities: mergeValidatedEntities(previous.entities, entities) }
          : previous,
      );
      return;
    }
    // The server refreshed its catalogue; the pages refetch on their own. A draft entry
    // for a table the source no longer has is dropped, exactly as the tick itself was.
    const listed = new Set(entities.map((entity) => entity.name));
    setForm((previous) =>
      previous
        ? { ...previous, draft: draftListedBy(previous.draft, listed, handNamed) }
        : previous,
    );
  };

  /** G3: a 409 `STALE_UPDATE` is said in place, with the reload the message asks for. */
  const showWriteError = (
    error: unknown,
    fallback: string,
    title = SAVE_FAILED_TITLE,
    sent?: RefusalSentBody,
  ) => {
    if (error instanceof ApiClientError && error.code === "STALE_UPDATE") setStaleConflict(true);
    showFormError(error, fallback, title, sent);
  };

  /** Just above the save bar: the reason, "Reference id {id}." and Copy reference. */
  const showFormError = (
    error: unknown,
    fallback: string,
    title = SAVE_FAILED_TITLE,
    // What the refused request sent, so a body check's index path names its field or table.
    sent?: RefusalSentBody,
  ) => {
    setFormErrorTitle(title);
    setPanelLines(
      refusalPanelLines(error, {
        fieldDefinitions: definition?.fieldDefinitions ?? [],
        entitySingular: definition?.entitySelection?.entityLabelSingular ?? "entity",
        entityPlural: definition?.entitySelection?.entityLabelPlural ?? "entities",
        ...(sent ? { sent } : {}),
      }),
    );
    const refused = refusalFieldErrors(error);
    setRefusedFields(refused);
    formError.show(error, fallback);
    // #1244: the field the refusal names is brought into view and focused, clear of the panel.
    if (refused.displayName) {
      firstField.current?.scrollIntoView?.({ block: "center" });
      firstField.current?.focus();
    }
  };

  /** #1092: an answer about values the form no longer holds goes with them. */
  const clearRefusal = () => {
    formError.clear();
    setPanelLines([]);
    setRefusedFields(NO_REFUSAL_FIELD_ERRORS);
    setTargetNameErrors(NO_TARGET_NAME_ERRORS);
  };

  const reloadFromServer = () => {
    // The pages too, or the grid would keep showing ticks from before the other save.
    invalidateDataflows(dataflowId ?? undefined);
    void detailQuery.refetch().then(() => setSeededFor(null));
  };

  /**
   * #927 follow-up (owner): Validate pressed at the bottom of a long form answers out of view.
   * `bar` marks a press of the save bar's Validate, so its answer brings the table list (after a
   * pass) or the first invalid field (after a failure) into view. A press at the top never scrolls.
   */
  const handleValidate = (source: "bar" | "inline" = "inline") => {
    if (!definition || !form) return;
    validateSource.current = source;
    setShowErrors(true);
    formError.clear();
    const sentFields = isNew ? fieldInputs() : changedFieldInputs();
    const validateFailed = (error: unknown) =>
      showFormError(error, "We could not validate this connection.", VALIDATE_FAILED_TITLE, {
        fieldIds: sentFields.map((field) => field.id),
      });

    const sentValues = form.values;
    setLastValidate(null);
    if (isNew) {
      validateUnsaved.mutate(
        { connectorName: definition.name, fields: sentFields },
        {
          onSuccess: (result) => {
            // SD3: a source that gave no verdict (a timeout) tested nothing about these values.
            const noVerdict = result.status === "failed" && isNoVerdict(result.errorCode);
            setLastValidate({
              at: result.at,
              status: result.status,
              message: result.message,
              values: sentValues,
              unsaved: false,
              listed: null,
              errorCode: result.errorCode,
              ...(result.referenceId ? { referenceId: result.referenceId } : {}),
              ...(noVerdict ? { noVerdict: true } : {}),
              ...(result.removedSelected ? { removedSelected: result.removedSelected } : {}),
            });
            if (noVerdict) return;
            applyValidation(
              result.status,
              result.message,
              result.errorCode,
              result.at,
              result.entities,
            );
          },
          onError: validateFailed,
        },
      );
      return;
    }

    revalidate.mutate(
      { fields: sentFields },
      {
        onSuccess: (result) => {
          takeServerAnswer(result.dataflow);
          // #1098: an older server answers no `recorded`; it recorded every test.
          const recorded = result.recorded !== false;
          const passed = result.validation.status === "success";
          // SD3: the server flags a no verdict on the stored values; a test of unsaved values
          // answers `recorded: false` without it, so the hub's own code decides there.
          const noVerdict =
            result.noVerdict === true || (!passed && isNoVerdict(result.validation.errorCode));
          const listed =
            !recorded && passed
              ? new Set(result.validation.entities.map((entity) => entity.name))
              : null;
          setLastValidate({
            at: result.validation.at,
            status: result.validation.status,
            message: result.validation.message,
            values: sentValues,
            errorCode: result.validation.errorCode,
            ...(result.referenceId ? { referenceId: result.referenceId } : {}),
            // SD2: a no-verdict answer is not a failed test of unsaved values, so it never says
            // "did not pass" or "imports stop".
            unsaved: !recorded && !noVerdict,
            ...(noVerdict ? { noVerdict: true } : {}),
            listed,
            ...(result.validation.removedSelected
              ? { removedSelected: result.validation.removedSelected }
              : {}),
          });
          // A test of unsaved values changed nothing saved: the saved answer and the table list
          // on screen stay the saved connection's own. A draft tick for a table these values do
          // not have goes now, as after a recorded pass, so the save never sends one (#1098 review).
          if (!recorded) {
            if (listed) {
              setForm((previous) =>
                previous
                  ? { ...previous, draft: draftListedBy(previous.draft, listed, handNamed) }
                  : previous,
              );
            }
            return;
          }
          applyValidation(
            result.validation.status,
            result.validation.message,
            result.validation.errorCode,
            result.validation.at,
            result.validation.entities,
          );
        },
        onError: validateFailed,
      },
    );
  };

  /** #2428: the rebuild question's lines for the landed reports a draft edits, or null. */
  const rebuildQuestionOf = (current: EditorForm): string[] | null => {
    if (isNew || !reportKind) return null;
    const edits = landedReportEdits(
      Object.entries(current.draft.editedFrom ?? {}).flatMap(([name, from]) => {
        const now = current.draft.settings[name]?.definition;
        return now ? [{ name, was: from.was, now, landed: from.landed }] : [];
      }),
    );
    if (edits.length === 0) return null;
    return rebuildSentences({
      edits,
      startDate: String(current.values.startDate ?? ""),
      historyDays: historyDaysOf(current.values),
      policy: detail?.schemaPolicy ?? DEFAULT_SCHEMA_POLICY,
    });
  };

  /** #2428: the rebuild question, unless the person has already confirmed it. */
  const rebuildQuestionUnlessConfirmed = (
    current: EditorForm,
    options?: { rebuildConfirmed?: boolean },
  ): string[] | null => (options?.rebuildConfirmed === true ? null : rebuildQuestionOf(current));

  /**
   * True when a refusal said at a field holds the save. FR-42: a failing rule does NOT stop the
   * save. It only decides whether the messages are on screen while the Draft is kept. The
   * exceptions: a typed Name that breaks its rule (the server refuses it, and it can never change
   * after the save), and two ticked tables that would land in one warehouse table (owner
   * 2026-10-01 23:40; the server refuses that too). Lead round: a table's own name that breaks its
   * rule; the server refuses it too. #1094: a Start date changed here to a day later than today is
   * said at the field; the server refuses it too, so nothing is sent until it is fixed. An
   * untouched saved one does not hold the save (`futureDayHeld`). #1047 #1198: a Display name the
   * server refuses is said at the field; no request goes.
   */
  const saveHeldAtField = (): boolean =>
    nameCollisions.size > 0 ||
    Boolean(targetNameInvalid) ||
    futureDayHeld ||
    Boolean(displayNameProblem) ||
    descriptionTooLong;

  const handleSave = (options?: { rebuildConfirmed?: boolean }) => {
    if (!definition || !form) return;
    // #1243: Save is off while Validate runs; a save then would drop the answer on its way.
    if (isValidating) return;
    setSavedOk(false);
    setShowErrors(true);
    formError.clear();

    if (saveHeldAtField()) return;
    // #2428 (R5, R13): an edit of a report that has loaded rebuilds its table on the next import;
    // the person is asked first, in numbers, with the connection's schema change policy.
    const rebuildQuestion = rebuildQuestionUnlessConfirmed(form, options);
    if (rebuildQuestion) {
      setRebuildLines(rebuildQuestion);
      return;
    }
    setPanelLines([]);
    setRefusedFields(NO_REFUSAL_FIELD_ERRORS);
    setTargetNameErrors(NO_TARGET_NAME_ERRORS);
    const refused = (error: unknown) => setTargetNameErrors(targetNameRefusals(error));
    const sentFields = fieldInputs();
    const sentFieldIds = sentFields.map((field) => field.id);
    if (isNew) {
      if (form.nameEdited && !isConnectionName(form.name)) return;
      const sentEntities = withoutIncremental(
        toEntityInputs(form.entities),
        entityIncrementalAllowed(definition),
      );
      createDataflow.mutate(
        {
          connectorName: definition.name,
          displayName: form.displayName,
          // Round 6: only a typed Name is sent; otherwise the server derives it from the Display
          // name and numbers it when another connection holds it.
          ...(form.nameEdited ? { name: form.name } : {}),
          description: form.description,
          imageTag: form.imageTag || undefined,
          // Round 6, S2: no refresh type; the server stores the connector's capability and the
          // trigger (a schedule row, Run now) decides full or incremental.
          fields: sentFields,
          entities: sentEntities,
          // #927: a validation that passed on these very values asks the server to run it again
          // as part of the create; the server's own answer decides the state, never this flag.
          ...(validatedUnchanged ? { validate: true } : {}),
        },
        {
          onSuccess: (created) => {
            // The address first (rehearsal 2026-10-09: it stayed on /connections/new, and a
            // reload showed an empty form). Nothing below can now keep it from moving, and it
            // REPLACES /new: the saved connection is not a page Back should return to empty.
            // The state updates below land in the same render, and the route keeps this editor
            // mounted for the new id (`keepEditorForCreated`, #1647), so it is seeded from the
            // answer and keeps the post-save guidance.
            keepEditorForCreated(created.id);
            navigate(absolutePath(connectionPath(created.id)), { replace: true });
            takeServerAnswer(created);
            // Posted for the new connection's page, the navigation to it already asked for.
            confirmSaved(created, connectionPath(created.id));
            setSeededFor(`edit:${created.id}`);
            seed(formFromDetail(definition, created));
            setSavedOk(true);
            setShowErrors(true);
            setValidation(created.lastValidation);
            setEntityQuery((previous) => ({
              ...DEFAULT_ENTITY_QUERY,
              pageSize: previous.pageSize,
            }));
          },
          onError: (error) => {
            refused(error);
            showFormError(error, "We could not save this connection.", SAVE_FAILED_TITLE, {
              fieldIds: sentFieldIds,
              entityNames: sentEntities.map((entity) => entity.name),
            });
          },
        },
      );
      return;
    }

    const finish = (saved: DataflowDetail) => {
      takeServerAnswer(saved);
      confirmSaved(saved);
      seed(formFromDetail(definition, saved));
      setSavedOk(true);
      setShowErrors(true);
      setValidation(saved.lastValidation);
    };

    const onUpdated = (updated: DataflowDetail) => {
      // #1098 review: the save's own pass replaced the table list with the one these values
      // found, so a tick for a table it did not list is not sent (the server would refuse
      // it) and the person is told.
      const listed =
        saveRetests && updated.lastValidation?.status === "success"
          ? (lastValidate?.listed ?? null)
          : null;
      const draft = listed ? draftListedBy(form.draft, listed, handNamed) : form.draft;
      const unlisted = listed ? draftUnlisted(form.draft, listed, handNamed) : 0;
      const finishSave = (saved: DataflowDetail) => {
        // The entity write answers without the save's test outcome; it is the PUT's.
        finish(
          updated.validationOnCreate
            ? { ...saved, validationOnCreate: updated.validationOnCreate }
            : saved,
        );
        // SD2/SD3: the save's own passing test removed selected tables the source no longer lists.
        const removedLine = updated.removedSelected
          ? removedSelectedSentence(updated.removedSelected, entityLabelsOf(definition))
          : null;
        if (removedLine) notice.warning(removedLine, { key: "removed-selected" });
        if (unlisted > 0) {
          notice.warning(unlistedTicksSentence(unlisted, entityLabelsOf(definition)), {
            key: "entity-unlisted",
          });
        }
      };
      const patch = draftToPatch(draft, entityIncrementalAllowed(definition));
      if (!patch) {
        finishSave(updated);
        return;
      }
      const patchedNames = (patch.entities ?? []).map((entity) => entity.name);
      // The stamp the PUT just answered with, because that write moved it.
      patchEntities.mutate(
        { ...patch, expectedUpdatedAt: updated.updatedAt },
        {
          onSuccess: finishSave,
          onError: (error) => {
            // The form landed and moved the stamp; the next save must name the new one.
            setLoadedUpdatedAt(updated.updatedAt);
            refused(error);
            showWriteError(error, "We saved the form but not the entities.", SAVE_FAILED_TITLE, {
              entityNames: patchedNames,
            });
          },
        },
      );
    };

    const runUpdate = (expectedUpdatedAt: string | undefined) =>
      updateDataflow.mutate(
        {
          displayName: form.displayName,
          description: form.description,
          imageTag: form.imageTag || undefined,
          refreshType: form.refreshType,
          fields: sentFields,
          // #1098: the Validate on these unsaved values recorded nothing; the server tests what
          // this save submits, and its own answer decides the state.
          ...(saveRetests ? { validate: true } : {}),
          expectedUpdatedAt,
        },
        {
          onSuccess: onUpdated,
          onError: (error) =>
            showWriteError(error, "We could not save this connection.", SAVE_FAILED_TITLE, {
              fieldIds: sentFieldIds,
            }),
        },
      );

    // R8 (#2428): removed reports go first, so the connection save may drop a property that only
    // they read; the rest of the draft follows the connection save as before.
    const removedNames = form.draft.removed.map((row) => row.name);
    if (removedNames.length === 0) {
      runUpdate(loadedUpdatedAt ?? undefined);
      return;
    }
    patchEntities.mutate(
      { remove: removedNames, expectedUpdatedAt: loadedUpdatedAt ?? undefined },
      {
        onSuccess: (afterRemove) => {
          setLoadedUpdatedAt(afterRemove.updatedAt);
          setForm((previous) =>
            previous ? { ...previous, draft: { ...previous.draft, removed: [] } } : previous,
          );
          runUpdate(afterRemove.updatedAt);
        },
        onError: (error) =>
          showWriteError(error, "We could not remove the reports.", SAVE_FAILED_TITLE, {
            entityNames: removedNames,
          }),
      },
    );
  };

  // ── The entity grid's data and intents ─────────────────────────────────────

  const entityView = entityQuery.selected;
  const entityTypeFilter = entityQuery.entityType;
  const pageSize = entityQuery.pageSize;

  /** The page to show: the server's, with the draft laid over it; or the local list's. */
  const grid = useMemo(() => {
    if (!form) {
      return {
        rows: [] as EntityRow[],
        total: 0,
        entityCount: 0,
        selectedCount: 0,
        viewCount: 0,
        selectedViewCount: 0,
      };
    }
    if (isNew) {
      const matching = form.entities.filter((row) =>
        matches(row, entityQuery.search, entityView, entityTypeFilter),
      );
      const start = (entityQuery.page - 1) * pageSize;
      return {
        rows: matching.slice(start, start + pageSize),
        total: matching.length,
        entityCount: form.entities.length,
        selectedCount: form.entities.filter((row) => row.isSelected).length,
        viewCount: form.entities.filter((row) => row.entityType === "view").length,
        selectedViewCount: form.entities.filter(
          (row) => row.entityType === "view" && row.isSelected,
        ).length,
      };
    }
    const draft = form.draft;
    const removed = new Set(draft.removed.map((row) => row.name));
    const served = (entityPage.data?.items ?? [])
      .filter((item) => !removed.has(item.name))
      .map((item) => {
        const row = toRow(item);
        const tick = draft.ticks[row.name];
        const settings = draft.settings[row.name];
        return {
          ...row,
          ...(settings ?? {}),
          isSelected: tick ? tick.now : row.isSelected,
        };
      });
    const added = isDefaultQuery ? draft.added : [];
    const delta = Object.values(draft.ticks).reduce(
      (sum, tick) => sum + Number(tick.now) - Number(tick.was),
      0,
    );
    // A tick remembers whether its row is a view, so a tick made on another page still counts.
    const viewDelta = Object.values(draft.ticks).reduce(
      (sum, tick) => (tick.view ? sum + Number(tick.now) - Number(tick.was) : sum),
      0,
    );
    // #2428: a removed report leaves the list at once; its removal is sent on Save.
    const removedSelected = draft.removed.filter((row) => row.isSelected).length;
    return {
      rows: [...added, ...served],
      total: (entityPage.data?.total ?? 0) + added.length - draft.removed.length,
      entityCount: serverCounts.entityCount + draft.added.length - draft.removed.length,
      selectedCount:
        serverCounts.selectedCount +
        delta +
        draft.added.filter((row) => row.isSelected).length -
        removedSelected,
      viewCount: serverCounts.viewCount,
      selectedViewCount: Math.max(serverCounts.selectedViewCount + viewDelta, 0),
    };
  }, [
    form,
    isNew,
    entityQuery,
    entityView,
    entityTypeFilter,
    pageSize,
    entityPage.data,
    serverCounts,
    isDefaultQuery,
  ]);
  // #2481: a report connection with a report on screen only needs validating, one with none needs
  // a report. The draft counts, so a report just added is not asked for again.
  const setupNeed = setupNeedOf(reportKind, grid.selectedCount);

  /**
   * Owner 2026-10-01 23:40: two ticked tables that would land in one warehouse table, found with
   * the loader's own rule (`findTargetNameCollisions`) BEFORE the save, so both rows say so and
   * Save waits. A new connection holds every row; a saved one holds the page on screen and its
   * unsaved additions, and the server's 409 `TABLE_NAME_TAKEN` (the same sentence) guards the
   * rest, other connections included.
   */
  const nameCollisions = form
    ? formNameCollisions(form, isNew, grid.rows, tableNaming)
    : NO_COLLISIONS;

  /**
   * Lead round: a selected table's typed warehouse name breaks the rule (the server refuses it
   * too), on the rows held here and in the draft of rows on other pages; Save waits.
   */
  const connectionName = form?.name.trim() || null;
  const entityNaming = useMemo<TableNaming>(
    () => ({ ...tableNaming, connectionName }),
    [tableNaming, connectionName],
  );

  const targetNameInvalid = form
    ? formTargetNameInvalid(form, isNew, grid.rows, connectionName)
    : false;

  // #1047 #1198: the Display name the server would refuse, said at the field before the save: its
  // rules and a name another connection of the instance already holds (compared by
  // `displayNameKey`: case and spaces ignored, as the server does). The name this connection
  // already has saved is never refused (#1672: a legacy case twin still saves its other edits,
  // as the server allows), and an empty one only turns Save off until it is typed.
  const takenDisplayNames = new Set(
    (listedConnections ?? []).filter((row) => row.id !== dataflowId).map((row) => row.displayName),
  );
  const displayNameUnchanged =
    !isNew &&
    detail !== undefined &&
    form !== null &&
    displayNameKey(form.displayName) === displayNameKey(detail.displayName);
  const displayNameTaken = displayNameUnchanged ? new Set<string>() : takenDisplayNames;
  const displayNameProblem = form ? displayNameIssue(form.displayName, displayNameTaken) : null;
  const displayNameMissing = displayNameProblem === DISPLAY_NAME_REQUIRED;
  const descriptionTooLong = (form?.description.length ?? 0) > DESCRIPTION_MAX;
  const descriptionMessage =
    refusedFields.description ?? (descriptionTooLong ? DESCRIPTION_TOO_LONG : undefined);

  const setQuery = (patch: Partial<EntitiesQuery>) =>
    setEntityQuery((previous) => ({ ...previous, ...patch }));

  const toggleRows = (names: string[], isSelected: boolean) => {
    setForm((previous) => {
      if (!previous) return previous;
      if (isNew) {
        const wanted = new Set(names);
        return {
          ...previous,
          entities: previous.entities.map((row) =>
            wanted.has(row.name) ? { ...row, isSelected } : row,
          ),
        };
      }
      const served = new Map((entityPage.data?.items ?? []).map((item) => [item.name, item]));
      const ticks = { ...previous.draft.ticks };
      const settings = { ...previous.draft.settings };
      const added = previous.draft.added.map((row) =>
        names.includes(row.name) ? { ...row, isSelected } : row,
      );
      for (const name of names) {
        const server = served.get(name);
        if (!server) continue;
        const was = ticks[name]?.was ?? server.isSelected;
        if (was === isSelected) delete ticks[name];
        else {
          ticks[name] = {
            was,
            now: isSelected,
            displayName: server.displayName,
            view: server.entityType === "view",
          };
        }
        if (settings[name]) settings[name] = { ...settings[name], isSelected };
      }
      return { ...previous, draft: { ...previous.draft, ticks, settings, added } };
    });
  };

  const changeRow = useCallback(
    (name: string, patch: Partial<EntityRow>) => {
      // Lead round: a new name is a new question; the server's last answer about the row goes.
      if (patch.targetName !== undefined) {
        setTargetNameErrors((errors) => {
          if (!errors.has(name)) return errors;
          const rest = new Map(errors);
          rest.delete(name);
          return rest;
        });
      }
      setForm((previous) => {
        if (!previous) return previous;
        if (isNew) {
          return {
            ...previous,
            entities: previous.entities.map((row) =>
              row.name === name ? { ...row, ...patch } : row,
            ),
          };
        }
        const addedIndex = previous.draft.added.findIndex((row) => row.name === name);
        if (addedIndex >= 0) {
          const added = previous.draft.added.map((row) =>
            row.name === name ? { ...row, ...patch } : row,
          );
          return { ...previous, draft: { ...previous.draft, added } };
        }
        const current = grid.rows.find((row) => row.name === name);
        if (!current) return previous;
        const drafted = previous.draft.settings[name];
        const next: EntitySettingsDraft = {
          isSelected: current.isSelected,
          incrementalEnabled: current.incrementalEnabled,
          watermarkColumn: current.watermarkColumn,
          keyColumns: current.keyColumns,
          // Lead round: a name typed before another setting changed stays in the draft.
          ...(drafted && drafted.targetName !== undefined
            ? { targetName: drafted.targetName }
            : {}),
          ...patch,
        };
        return {
          ...previous,
          draft: { ...previous.draft, settings: { ...previous.draft.settings, [name]: next } },
        };
      });
    },
    [isNew, grid.rows],
  );

  /** #2428: a report as the list shows it: a new connection's, one added or one on the page. */
  const reportRowOf = (name: string): EntityRow | undefined =>
    form
      ? [...(isNew ? form.entities : []), ...form.draft.added, ...grid.rows].find(
          (row) => row.name === name,
        )
      : undefined;

  /** #2428: the table a report lands in, as the Remove question names it. */
  const reportTableOf = (row: EntityRow): string =>
    targetTableName({
      ...entityNaming,
      entityName: row.name,
      ...(row.tableNameCase ? { tableNameCase: row.tableNameCase } : {}),
      override: row.targetName ?? null,
    });

  /**
   * #2428: Add (and Duplicate) puts a new report in the list, ticked; Edit changes a report's
   * definition in the draft, remembering what a saved one held, so Save can ask before it
   * rebuilds a table that has loaded.
   */
  const submitReport = (mode: ReportDrawerMode, report: ReportDrawerSubmit) => {
    setForm((previous) => {
      if (!previous) return previous;
      if (mode === "add") {
        const row = reportRow(report);
        if (isNew) return { ...previous, entities: inEntityOrder([...previous.entities, row]) };
        const removedRow = previous.draft.removed.find((removed) => removed.name === report.name);
        if (removedRow)
          return { ...previous, draft: readdRemoved(previous.draft, removedRow, report) };
        return {
          ...previous,
          draft: { ...previous.draft, added: [...previous.draft.added, row] },
        };
      }
      if (isNew) {
        return {
          ...previous,
          entities: previous.entities.map((row) =>
            row.name === report.name ? { ...row, definition: report.definition } : row,
          ),
        };
      }
      if (previous.draft.added.some((row) => row.name === report.name)) {
        return {
          ...previous,
          draft: {
            ...previous.draft,
            added: previous.draft.added.map((row) =>
              row.name === report.name ? { ...row, definition: report.definition } : row,
            ),
          },
        };
      }
      const current = grid.rows.find((row) => row.name === report.name);
      if (!current) return previous;
      const drafted = previous.draft.settings[report.name];
      const editedFrom = previous.draft.editedFrom ?? {};
      const served = (entityPage.data?.items ?? []).find((item) => item.name === report.name);
      return {
        ...previous,
        draft: {
          ...previous.draft,
          settings: {
            ...previous.draft.settings,
            [report.name]: {
              isSelected: current.isSelected,
              incrementalEnabled: current.incrementalEnabled,
              watermarkColumn: current.watermarkColumn,
              keyColumns: current.keyColumns,
              ...(drafted?.targetName === undefined ? {} : { targetName: drafted.targetName }),
              definition: report.definition,
            },
          },
          editedFrom: editedFrom[report.name]
            ? editedFrom
            : {
                ...editedFrom,
                [report.name]: {
                  was: served?.definition ?? current.definition ?? report.definition,
                  landed: entityHasLanded(current),
                },
              },
        },
      };
    });
  };

  /** #2428: an unsaved report goes at once; a saved one after the Remove question. */
  const removeReport = (name: string) => {
    if (!form) return;
    const row = reportRowOf(name);
    if (!row) return;
    const unsaved = isNew || form.draft.added.some((added) => added.name === name);
    if (unsaved) {
      setForm((previous) =>
        previous
          ? {
              ...previous,
              entities: previous.entities.filter((entity) => entity.name !== name),
              draft: {
                ...previous.draft,
                added: previous.draft.added.filter((added) => added.name !== name),
              },
            }
          : previous,
      );
      return;
    }
    setRemoveTarget({ name, table: reportTableOf(row), landed: entityHasLanded(row) });
  };

  /** #2428: the confirmed removal of a saved report, sent first on the next Save (R8). */
  const confirmRemoveReport = (name: string) => {
    setForm((previous) => {
      if (!previous) return previous;
      const row = grid.rows.find((candidate) => candidate.name === name);
      if (!row) return previous;
      const tick = previous.draft.ticks[name];
      const { [name]: _tick, ...ticks } = previous.draft.ticks;
      const { [name]: _settings, ...settings } = previous.draft.settings;
      // `editedFrom` is kept: with no drafted definition it asks nothing, and a re-add of the
      // same name in this draft compares with what the saved report held (`readdRemoved`).
      return {
        ...previous,
        draft: {
          ...previous.draft,
          ticks,
          settings,
          removed: [...previous.draft.removed, { ...row, isSelected: tick?.was ?? row.isSelected }],
        },
      };
    });
  };

  /**
   * "Select all N matching". For a saved connection this is the one entity action that
   * writes at once: the server resolves the search against the same predicate the listing
   * used, and the rest of the draft goes up in the same request. #1414: the button overrides
   * the unsaved ticks of the rows it covers (the server applies name-level ops after
   * search-level ones, so they would undo it); a tick outside the match is saved as it is.
   */
  const selectAllMatching = (isSelected: boolean) => {
    if (!form) return;
    // #1098 review: it saves, so it waits whenever Save does (a live import, a running Validate).
    if (!isNew && saveOffReason(importLive, isValidating)) return;
    if (isNew) {
      setForm((previous) =>
        previous
          ? {
              ...previous,
              entities: previous.entities.map((row) =>
                matches(row, entityQuery.search, entityView, entityTypeFilter)
                  ? { ...row, isSelected }
                  : row,
              ),
            }
          : previous,
      );
      return;
    }
    const matching = {
      search: entityQuery.search,
      selected: entityView,
      entityType: entityTypeFilter,
    };
    const outside = draftOutsideMatch(form.draft, entityQuery.search, entityView, entityTypeFilter);
    // #1414: the grid lists the hand-added rows under the default query and counts them in the
    // button, so the button ticks or unticks them too (the server only knows the stored rows).
    // The default query is Type "All", so under Tables or Views a hand-added row is never touched.
    const draftToSend: EntityDraft = isDefaultQuery
      ? { ...outside, added: outside.added.map((row) => ({ ...row, isSelected })) }
      : outside;
    const body: PatchEntitiesBody = {
      ...(draftToPatch(draftToSend, !definition || entityIncrementalAllowed(definition)) ?? {}),
      ...(isSelected ? { selectMatching: matching } : { deselectMatching: matching }),
      expectedUpdatedAt: loadedUpdatedAt ?? undefined,
    };
    patchEntities.mutate(body, {
      onSuccess: (saved) => {
        formError.clear();
        takeServerAnswer(saved);
        // Said in the notice (P13b r2): the grid's matching count does not change on a select
        // all and its "X of Y selected" badge is not live, so without this the action is silent
        // to a screen reader. Keyed, so pressing it again replaces rather than stacks.
        // The grid counts the hand-added rows only under the default query.
        const matchingCount = grid.total - (isDefaultQuery ? form.draft.added.length : 0);
        const labels = definition?.entitySelection;
        const noun = (
          matchingCount === 1
            ? (labels?.entityLabelSingular ?? "entity")
            : (labels?.entityLabelPlural ?? "entities")
        ).toLowerCase();
        // #1129: the bulk action saved, so the notice says so and names the setup it left, and
        // the earlier "Connection saved" notice (which may say no tables are selected) goes.
        notice.dismiss(SAVED_NOTICE_KEY);
        notice.success(
          [
            `Saved. ${matchingCount.toLocaleString()} matching ${noun} ${
              isSelected ? "selected" : "cleared"
            }. ${saved.selectedCount.toLocaleString()} selected in total.`,
            bulkSetupSentence(saved, (labels?.entityLabelPlural ?? "entities").toLowerCase()),
          ]
            .filter(Boolean)
            .join(" "),
          { key: "entity-selection" },
        );
        // The draft went up with the request; the rest of the form stays as it is.
        setForm((previous) => (previous ? { ...previous, draft: EMPTY_DRAFT } : previous));
        setBaseline((previous) => (previous ? { ...previous, draft: EMPTY_DRAFT } : previous));
        if (!isDefaultQuery || entityView !== "all" || entityTypeFilter !== "all")
          setQuery({ page: 1 });
      },
      onError: (error) =>
        showWriteError(
          error,
          "We could not change the selection.",
          "We could not change the selection",
          { entityNames: (body.entities ?? []).map((entity) => entity.name) },
        ),
    });
  };

  // #1242: a reader who opens the address of a new connection gets the reason, not the picker.
  if (isNew && !canWrite) {
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        title="You cannot add a connection"
        message="You can view connections. Ask someone who can change connections in this instance to add one."
        testId="permission-denied"
      />
    );
  }

  if (idMalformed || (!isNew && isNotFoundError(detailQuery.error))) {
    return (
      <RecordNotFound
        noun="connection"
        listLabel="Back to Connections"
        listPath={ROUTES.connections}
        testId="connection-not-found"
      />
    );
  }

  if (isNew && !connectorName) return <ConnectorPicker />;

  if (definitionQuery.isLoading || detailQuery.isLoading || !form || !definition) {
    const failure = (detailQuery.error ?? definitionQuery.error) as ApiClientError | null;
    return editorNotReady(failure, () => {
      void detailQuery.refetch();
      void definitionQuery.refetch();
    });
  }

  // T-51: a link straight to a connector the tenant does not offer for new connections (the
  // picker hides it). Called, not rendered as a component: the page returns the bar itself.
  const notOffered = notOfferedForNew(isNew, definition, navigate);
  if (notOffered !== null) return notOffered;

  const state = stateFromLastWrite ?? detail?.state ?? "draft";
  // The list's one status (owner feedback, 2026-09-28): the setup state from the latest write,
  // merged with this connection's import as the header already follows it.
  const status = isNew
    ? connectionStatusOf(statusInputOf(state, detail, listedRun), null, null)
    : connectionStatusOf(
        statusInputOf(state, detail, listedRun),
        importActivity.phase,
        importActivity.runId,
      );
  const entitySelection = definition.entitySelection;
  const supportsIncremental = entityIncrementalAllowed(definition);
  const browseField = fieldById(definition, browseFieldId);
  const selectedCount = grid.selectedCount;
  const storedCredential = detail?.fields.some((field) => field.hasStoredSecret) ?? false;
  // #1107: below `dataflows.write` the server withholds the source's host, user name and email.
  // #1932: the fields the environment pins (an editor's read of the definition only).
  const pinnedFields = pinnedFieldsOf(definition);
  // Review of #2429: a saved connection that stores another address reads the change at the field.
  const pinnedChanged =
    !isNew && definition && detail
      ? pinnedFieldsChanged(storedValuesOf(definition, detail), pinnedFields)
      : undefined;
  const withheldFields = new Set(
    (detail?.fields ?? []).filter((field) => field.withheld).map((field) => field.id),
  );
  const catalogueIsEmpty = grid.entityCount === 0;
  // 2026-09-30: a declared connector's tables are the definition's, not the source's discovery.
  const catalogueIsDeclared = definition.catalogueSource === "declared";
  const entityPluralLower = (entitySelection?.entityLabelPlural ?? "entities").toLowerCase();
  // The saved tag, not the form's: it stays offered after another version is picked (review r2 N10).
  const imageTagOptions = versionOptions(definition, baseline?.imageTag ?? form.imageTag);
  // A typed Name is checked as it is typed; a derived one always satisfies the rule.
  const connectionNameInvalid = typedNameInvalid(isNew, form);

  // #1198: an empty Display name keeps Save off, and the bar says why in words, not only a tooltip.
  const savedStatus = saveStatusOf(isDirty, detail !== undefined, validation, zone);
  const saveStatus = displayNameMissing
    ? `${savedStatus.replace(/\.$/, "")}. ${ENTER_DISPLAY_NAME}`
    : savedStatus;
  // The server's own reason first (a 409 for a name taken since the list was read), then the
  // rules; the empty-name message shows once the field is left, or after a Save or Validate, as
  // the other required fields do (#1198).
  const displayNameMessage =
    refusedFields.displayName ??
    (displayNameProblem &&
    (!displayNameMissing || showErrors || touchedFields.has(DISPLAY_NAME_TOUCHED))
      ? displayNameProblem
      : undefined);

  const credentialGroupFooter = (groupId: string) => {
    const credentialFields = definition.fieldDefinitions.filter(
      (field) => field.groupId === groupId && field.isCredential,
    );
    if (credentialFields.length === 0 || !storedCredential) return null;
    // FR-43: the re-validate action sits with the credentials it
    // would otherwise ask the user to retype.
    const revalidateBlock = canValidate ? (
      <div className="flex flex-col gap-1">
        <div>
          <SkyButton
            appearance="secondary"
            size="small"
            icon={RetryIcon}
            testId="revalidate-stored"
            loading={revalidate.isPending}
            onClick={() => handleValidate()}
          >
            Re-validate with stored credentials
          </SkyButton>
        </div>
        <SkyText size={200} color="muted">
          The stored value stays in Key Vault. Leave the field as it is to keep it.
        </SkyText>
      </div>
    ) : null;
    // 2026-09-28 cred-check: the daily check's line, under the first group that
    // holds a credential, and "Expires on" for a credential the connector marks as
    // expiring (a client secret, an API token) once one is stored.
    const firstCredentialGroup =
      definition.fieldDefinitions.find((field) => field.isCredential)?.groupId === groupId;
    const expiring = credentialFields.filter(
      (field) =>
        field.expires === true &&
        detail?.fields.some((row) => row.id === field.id && row.hasStoredSecret),
    );
    const showCheck = firstCredentialGroup && credentials.data !== undefined;
    if (!revalidateBlock && !showCheck && expiring.length === 0) return null;
    return (
      <div className="flex flex-col gap-2">
        {revalidateBlock}
        {showCheck && credentials.data ? (
          <CredentialCheckLine
            target={credentialCheck}
            checksEnabled={credentials.data.checksEnabled}
            testId="credential-check-line"
          />
        ) : null}
        {expiring.map((field) => (
          <CredentialExpiryEditor
            key={field.id}
            label={field.name}
            expiry={credentialCheck?.expiries.find((row) => row.field === field.id) ?? null}
            canEdit={canWrite}
            pending={setCredentialExpiry.isPending}
            onSave={(expiresOn) => setCredentialExpiry.mutateAsync({ field: field.id, expiresOn })}
            testId={`credential-expiry-${field.id}`}
          />
        ))}
      </div>
    );
  };

  /** Disable, or Enable while disabled: the one dialog both the button and the menu item open. */
  const openEnableToggle = () => {
    if (!detail) return;
    toggle.open(
      state === "disabled"
        ? {
            id: detail.id,
            name: detail.displayName,
            mode: "enable",
            disabled: detail.disabled ?? null,
          }
        : { id: detail.id, name: detail.displayName, mode: "disable" },
    );
  };

  /** #977: Cancel run opens its confirm for the run the header is showing. */
  const openCancelRun = () => {
    if (!detail || !importActivity.runId) return;
    setCancelTarget({
      runId: importActivity.runId,
      dataflowId: detail.id,
      name: detail.displayName,
    });
  };

  const renderEnableToggle = (): ReactNode => {
    if (isNew || !detail || !canWrite) return null;
    const enabling = state === "disabled";
    const waitsForImport = !enabling && importLive;
    const button = (
      <SkyButton
        appearance="secondary"
        icon={enabling ? PlayIcon : PauseIcon}
        testId={enabling ? "connection-enable" : "connection-disable"}
        disabled={waitsForImport}
        onClick={openEnableToggle}
      >
        {enabling ? "Enable" : "Disable"}
      </SkyButton>
    );
    // #975: a disabled button fires no pointer events, so the tooltip listens on its wrapper.
    return waitsForImport ? (
      <SkyTooltip content={NOT_WHILE_IMPORTING}>
        <span className="inline-flex">{button}</span>
      </SkyTooltip>
    ) : (
      button
    );
  };

  /**
   * B-53: below `lg` the header keeps Import now as its one action button and folds the secondary
   * ones (Import with options, Disable or Enable) into this menu, so the badges and buttons do not
   * wrap under the title on a tablet. At `lg` and above the buttons are drawn as before and this
   * menu is hidden; both are in the page, and CSS (not a script) chooses which is shown.
   */
  const renderOverflowMenu = (): ReactNode => {
    if (isNew || !detail) return null;
    const importOptions = Boolean(dataflowId && canStartImport);
    const cancelRun = Boolean(dataflowId && canCancelImport && importLive && importActivity.runId);
    if (!importOptions && !canWrite && !cancelRun) return null;
    const importOptionsOff = importOffReason(
      state,
      isActivityLive(importActivity),
      destinationRunReason,
      setupNeed,
    );
    const enabling = state === "disabled";
    return (
      <div className="lg:hidden">
        <SkyMenu
          align="end"
          trigger={
            <SkyButton
              appearance="secondary"
              testId="connection-header-more"
              aria-label="More actions for this connection"
            >
              ⋯
            </SkyButton>
          }
          items={[
            ...(importOptions
              ? [
                  {
                    key: "import-options",
                    label: "Import with options",
                    icon: <PlayIcon className="h-4 w-4" />,
                    disabled: importOptionsOff !== null || importStarting,
                    // A menu item has no tooltip: it says why in the item (#1080, #1081).
                    ...(importOptionsOff ? { description: importOptionsOff } : {}),
                    onClick: () => setImportOptionsOpen(true),
                  },
                ]
              : []),
            ...(cancelRun
              ? [
                  {
                    key: "cancel-run",
                    label: "Cancel run",
                    icon: <CancelIcon className="h-4 w-4" />,
                    onClick: openCancelRun,
                  },
                ]
              : []),
            ...(canWrite
              ? [
                  {
                    key: enabling ? "enable" : "disable",
                    label: enabling ? "Enable connection" : "Disable connection",
                    icon: enabling ? (
                      <PlayIcon className="h-4 w-4" />
                    ) : (
                      <PauseIcon className="h-4 w-4" />
                    ),
                    ...(!enabling && importLive
                      ? { disabled: true, description: NOT_WHILE_IMPORTING }
                      : {}),
                    onClick: openEnableToggle,
                  },
                ]
              : []),
          ]}
        />
      </div>
    );
  };

  const renderTopbar = () => (
    <TopbarActionsPortal>
      <ConnectionStatusBadge status={status} testId="header-state-badge" need={setupNeed} />
      {/* Only when something needs attention; the Status word itself does not change. */}
      {isNew ? null : (
        <CredentialAttentionBadge target={credentialCheck} testId="header-credential-badge" />
      )}
      {isNew ? null : <ImportStepText activity={importActivity} />}
      {/* The page's one polite line for this connection's import (review r1, B1): mounted
          before an import exists and kept through its end, outside the panel it drives. It
          names the SAVED connection: the Name field being typed never changes it (r2 B1'). */}
      {isNew ? null : (
        <ImportLiveLine
          activity={importActivity}
          connectionName={detail?.displayName || "This connection"}
        />
      )}
      {!isNew && dataflowId && canStartImport ? (
        <ImportNowButton
          state={state}
          active={isActivityLive(importActivity)}
          pending={importStarting}
          onImport={() => startImportHere(dataflowId)}
          need={setupNeed}
        />
      ) : null}
      {/* Round 6, S5: Run now left the Runs page; its Full refresh and Choose tables open
          here, on the connection they are about. */}
      {!isNew && dataflowId && canStartImport ? (
        <div className="max-lg:hidden lg:contents">
          {/* #1080, #1081: off for Import now's reasons, the destination's among them, and says
              which. */}
          <ImportOptionsButton
            need={setupNeed}
            state={state}
            active={isActivityLive(importActivity)}
            pending={importStarting}
            onOpen={() => setImportOptionsOpen(true)}
          />
        </div>
      ) : null}
      {/* #977: stop the import in flight (runs.cancel); the dialog confirms. */}
      {dataflowId && canCancelImport && importLive && importActivity.runId ? (
        <div className="max-lg:hidden lg:contents">
          <SkyButton
            appearance="secondary"
            icon={CancelIcon}
            testId="connection-cancel-run"
            onClick={openCancelRun}
          >
            Cancel run
          </SkyButton>
        </div>
      ) : null}
      <div className="max-lg:hidden lg:contents">{renderEnableToggle()}</div>
      {renderOverflowMenu()}
      {/* Alert routing (owner 2026-09-28): the caller's own Watch, for every role that reads
          the connection, beside Disable and Enable. */}
      {!isNew && detail ? (
        <WatchBadge targetType="dataflow" targetId={detail.id} name={detail.displayName} />
      ) : null}
      <SkyButton
        appearance="subtle"
        icon={ChevronLeftIcon}
        testId="back-to-connections"
        onClick={() => navigate(absolutePath(ROUTES.connections))}
      >
        All connections
      </SkyButton>
    </TopbarActionsPortal>
  );

  // #787: the last import was held back by a readiness gate: say why and what comes next.
  const waiting =
    status === "waiting" && listedRun ? connectionWaitingOf({ lastRun: listedRun }) : null;

  const renderBanners = () => (
    <>
      {waiting ? (
        <div className="mb-4">
          <SkyMessageBar
            intent="info"
            layout="multiline"
            testId="connection-waiting-banner"
            title={`Waiting: ${waiting.label}`}
            message={`${waitingLine(waiting)} ${
              listedRun?.outcomeRunId
                ? "Nothing was loaded by the last settled import."
                : "Nothing was loaded by the last import."
            }`}
          />
        </div>
      ) : null}

      {!isNew && state === "disabled" ? (
        <div className="mb-4">
          <SkyMessageBar
            intent="info"
            layout="multiline"
            testId="connection-disabled-banner"
            title="This connection is disabled"
            message={
              detail?.disabled
                ? `${withFinalStop(detail.disabled.reason)} Import now is off and every schedule skips it until it is enabled.`
                : "Import now is off and every schedule skips it until it is enabled."
            }
          />
        </div>
      ) : null}

      {!isNew && schemaDecisionsNeeded ? (
        <div className="mb-4">
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="schema-changes-banner"
            title="Source columns changed"
            message={schemaDecisionsNeeded}
            actions={
              <SkyButton
                size="small"
                testId="schema-changes-review"
                onClick={() => setSchemaDrawer({ entityName: null })}
              >
                Review changes
              </SkyButton>
            }
          />
        </div>
      ) : null}

      {staleConflict ? (
        <div className="mb-4">
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="connection-stale"
            title="Someone else saved this connection first"
            message="Reload to see their changes. Your unsaved edits here will be discarded."
            actions={
              <SkyButton
                size="small"
                icon={RetryIcon}
                testId="connection-reload"
                onClick={reloadFromServer}
              >
                Reload
              </SkyButton>
            }
          />
        </div>
      ) : null}

      {readOnly ? (
        <div className="mb-4">
          <SkyMessageBar
            intent="info"
            layout="multiline"
            testId="connection-read-only"
            title="You can read this connection but not change it"
            message="Ask someone who can change connections in this instance."
          />
        </div>
      ) : null}
    </>
  );

  const renderConnectionTabs = () => (
    <>
      {isNew ? null : (
        <fieldset
          aria-label="What to show for this connection"
          className="min-w-0 mb-4 flex items-center gap-2 border-b border-border pb-2"
          data-testid="connection-tabs"
        >
          {(["configuration", "runs"] as const).map((value) => (
            <SkyButton
              key={value}
              appearance={tab === value ? "secondary" : "subtle"}
              size="small"
              aria-pressed={tab === value}
              onClick={() => selectTab(value)}
              testId={`connection-tab-${value}`}
            >
              {CONNECTION_TAB_LABELS[value]}
            </SkyButton>
          ))}
        </fieldset>
      )}
    </>
  );

  const renderRunsPanel = () => (
    <>
      {tab === "runs" && detail ? (
        <section aria-label={CONNECTION_TAB_LABELS.runs} data-testid="connection-tabpanel-runs">
          {/* The last import leads the Runs tab, above its history (owner feedback, 2026-09-28:
              the Configuration tab opens on the configuration). The header keeps the status and
              Import now, so a running import is visible from either tab. */}
          {importActivity.runId || detail.lastRunId ? (
            <div className="mb-4">
              <LastRunPanel
                dataflowId={detail.id}
                lastRunId={importActivity.runId ?? detail.lastRunId}
                lastRefreshAt={detail.lastRefreshAt}
                activity={importActivity}
                connectionName={detail.displayName}
                vocabulary={importVocabulary}
                // Review r1 S1: the card's badge is this connection's own outcome, as the
                // header's; the card matches the run id itself.
                listedRun={listedRun}
              />
            </div>
          ) : null}
          {/* The header's state, not the cached row's: right after a save that made the
              connection Ready, the cache still says Draft until its refetch lands. */}
          <ConnectionRunsTab
            need={setupNeed}
            dataflow={{ ...detail, state }}
            // The empty state's Import now is the header's: it starts in place, the page stays,
            // and it is off for the same reasons (ux-s10 §2).
            importAction={
              dataflowId
                ? {
                    onImport: () => startImportHere(dataflowId),
                    pending: importStarting,
                    active: isActivityLive(importActivity),
                  }
                : undefined
            }
          />
        </section>
      ) : null}
    </>
  );

  // Round 8: a tested connection whose target fields the user has changed but not saved. Saving
  // keeps the stored credentials and marks the connection untested (`TARGET_CHANGED`).
  // #1098 review: only a save that tests these very values itself (`saveRetests`, after a pass)
  // can keep it tested, so only then is there nothing to warn about. A pass on screen followed
  // by any other edit, or by a Start date the form refuses, still warns.
  const testedHere = saveRetests && lastValidate?.status === "success";
  const unsavedTargetChanges =
    !isNew && baseline && validation?.status === "success" && !testedHere
      ? changedTargetFieldNames(definition.fieldDefinitions, form.values, baseline.values)
      : [];

  const renderValidationNotes = () => {
    const failed = validation?.status === "failed" ? validation : null;
    const storedFailed =
      failed &&
      failed.errorCode !== TARGET_CHANGED_CODE &&
      failed.errorCode !== CREDENTIAL_MISSING_CODE
        ? failed
        : null;
    // #2439: the no-verdict answer of the Validate on screen, while the form is as it was tested.
    const noVerdictShown = lastValidate?.noVerdict && !changedSinceValidate ? lastValidate : null;
    return (
      <>
        {stateNoteCards(failed, unsavedTargetChanges, readOnly)}
        {failureNoteCards({
          storedFailed,
          noVerdictShown,
          connectorName: definition.name,
          fields: definition.fieldDefinitions,
          zone,
          readOnly,
        })}
      </>
    );
  };

  const renderValidationSuccess = () => (
    <>
      {validation && validation.status === "success" ? (
        <div className="mb-4">
          <SkyMessageBar
            intent="success"
            // Walk 2026-10-02, D9: the library's single line cut a short message's tail.
            layout="multiline"
            testId="validation-succeeded"
            className={VALIDATION_PASSED_CARD}
            message={joinParts(
              validatedAtSentence(validation.at, zone, "Last validated"),
              validation.message,
              // #1234: a Viewer cannot choose; #1220: a connection that already has tables
              // chosen has nothing left to be told.
              readOnly || selectedCount > 0
                ? ""
                : `Choose the ${entityPluralLower} to import on the right.`,
            )}
          />
        </div>
      ) : null}
    </>
  );

  const renderVersionField = (): ReactNode => {
    if (imageTagOptions.length > 1) {
      return (
        <SkyDropdown
          testId="connection-image-tag"
          label="Connector version"
          options={imageTagOptions}
          selectedOptions={form.imageTag ? [form.imageTag] : []}
          disabled={readOnly}
          onOptionSelect={(selected) =>
            setForm((previous) =>
              previous ? { ...previous, imageTag: selected[0] ?? "" } : previous,
            )
          }
        />
      );
    }
    if (imageTagOptions.length === 1) {
      return (
        // Release A walk (dev 66fb913): with one choice there is nothing to pick, but the
        // editor still says which version the connection runs, in the dropdown's words.
        <fieldset
          aria-labelledby="connection-image-tag-label"
          className="min-w-0 flex flex-col gap-1"
          data-testid="connection-image-tag-readonly"
        >
          <SkyText id="connection-image-tag-label" size={300} weight="semibold" block>
            Connector version
          </SkyText>
          <SkyText size={300} block testId="connection-image-tag-value">
            {imageTagOptions[0]!.text}
          </SkyText>
        </fieldset>
      );
    }
    return null;
  };

  const renderRefreshNote = () => (
    <>
      {definition.isIncrementalSupported ? (
        // Round 6, S2: the trigger decides the mode (each schedule row, and Run now), so
        // the Full load / Incremental radio is gone; one read-only line says what the
        // connection does. S3: a note, not a gate, while a table has not landed.
        <div className="flex flex-col gap-1">
          <SkyText size={300} weight="semibold" block>
            Refresh
          </SkyText>
          <SkyText size={200} color="muted" block testId="connection-refresh-type">
            {definition.readsWholeDays
              ? CONNECTION_REFRESH_LINE_WHOLE_DAYS
              : CONNECTION_REFRESH_LINE}
          </SkyText>
          {(() => {
            const note = detail
              ? firstImportNote(detail, definition.readsWholeDays === true)
              : FIRST_IMPORT_HINT;
            return note ? (
              <SkyText size={200} color="muted" block testId="connection-first-import-hint">
                {note}
              </SkyText>
            ) : null;
          })()}
        </div>
      ) : (
        // #1077: a connector with no incremental mode says so, in the schedule row's words,
        // never the incremental line, whatever refresh type the connection stores.
        <div className="flex flex-col gap-1">
          <SkyText size={300} weight="semibold" block>
            Refresh
          </SkyText>
          <SkyText size={200} color="muted" block testId="connection-refresh-type">
            {FULL_ONLY_IMPORT_NOTE}
          </SkyText>
        </div>
      )}
    </>
  );

  const renderEmptyCatalogue = () => (
    <SkyCard className="p-6" testId="entities-empty">
      {catalogueIsDeclared ? (
        <>
          <SkyText as="h4" size={300} weight="semibold" block>
            Validate the connection to choose its {entityPluralLower}
          </SkyText>
          <SkyText as="p" size={300} color="muted" block className="mt-2">
            This connector defines its {entityPluralLower}. skyData lists them here once the
            credentials are accepted. Fill in the connection details on the left, then validate.
          </SkyText>
        </>
      ) : (
        <>
          <SkyText as="h4" size={300} weight="semibold" block>
            Validate the connection to list its {entityPluralLower}
          </SkyText>
          <SkyText as="p" size={300} color="muted" block className="mt-2">
            skyData reads the list from the source when validation succeeds. Fill in the server and
            credentials on the left, then validate.
          </SkyText>
        </>
      )}
      {canValidate && !readOnly ? (
        <div className="mt-4">
          <SkyButton
            appearance="primary"
            testId="validate-from-empty"
            loading={isValidating}
            onClick={() => handleValidate()}
          >
            Validate connection
          </SkyButton>
        </div>
      ) : null}
    </SkyCard>
  );

  /** The grid's optional props: each is passed only when it applies. */
  const entityTableOptions = () => ({
    ...(!isNew && dataflowId && state === "ready" && canStartImport && tableRunsOn
      ? { tableActions: definition.isIncrementalSupported ? tableActions : resyncOnlyActions }
      : {}),
    ...(!isNew && schemaCounts.size > 0
      ? { schemaCounts, onOpenSchemaChanges: openSchemaChanges }
      : {}),
    ...(nameCollisions.size > 0 ? { nameCollisions } : {}),
    ...(targetNameErrors.size > 0 ? { targetNameErrors } : {}),
  });

  const renderFormSection = () => (
    <section aria-labelledby="connection-form-heading" className="min-w-0" ref={formSection}>
      <SkyText
        as="h3"
        id="connection-form-heading"
        size={300}
        weight="semibold"
        block
        className="mb-3"
      >
        Connection details
      </SkyText>

      {renderValidationNotes()}

      {renderValidationSuccess()}

      <div className="flex flex-col gap-4">
        <SkyInput
          ref={firstField}
          testId="connection-name"
          label="Display name"
          required
          value={form.displayName}
          disabled={readOnly}
          hint={
            readOnly
              ? undefined
              : "Letters, digits, spaces, hyphen or underscore. Unique in this instance. You can change it later."
          }
          validationState={displayNameMessage ? "error" : "none"}
          validationMessage={displayNameMessage}
          onBlur={() => touchField(DISPLAY_NAME_TOUCHED)}
          onChange={(event) => {
            if (refusedFields.displayName) {
              setRefusedFields(({ displayName: _retyped, ...rest }) => rest);
            }
            setForm((previous) =>
              previous
                ? {
                    ...previous,
                    displayName: event.target.value,
                    // Round 6: a new connection's Name follows the Display name until it
                    // is typed into, as the tenant's Name does.
                    ...(isNew && !previous.nameEdited
                      ? {
                          name: event.target.value.trim()
                            ? connectionNameFromDisplayName(event.target.value)
                            : "",
                        }
                      : {}),
                  }
                : previous,
            );
          }}
        />

        <SkyInput
          testId="connection-table-prefix"
          label="Name"
          value={form.name}
          // B-74: read only, not disabled. A disabled field is dimmed to half opacity, which left
          // the connection's main label hard to read in dark mode; a read-only one keeps its
          // text contrast and can still be focused and copied.
          readOnly={readOnly || !isNew}
          hint={connectionNameHint(
            form.name,
            tableNaming,
            // The catalogue's first page as stored, not the grid: a search that shows no table
            // must not change how the tables are named.
            catalogueHasSchemas(definition.catalogueSource, [
              ...(isNew ? form.entities : (detail?.entities ?? [])),
              ...(isNew ? [] : form.draft.added),
            ]),
          )}
          validationState={connectionNameInvalid || refusedFields.name ? "error" : "none"}
          validationMessage={
            connectionNameInvalid ? CONNECTION_NAME_RULE : (refusedFields.name ?? undefined)
          }
          onChange={(event) => {
            if (refusedFields.name) setRefusedFields(({ name: _retyped, ...rest }) => rest);
            setForm((previous) =>
              previous ? { ...previous, name: event.target.value, nameEdited: true } : previous,
            );
          }}
        />

        <SkyTextarea
          testId="connection-description"
          label="Description"
          value={form.description}
          disabled={readOnly}
          hint={
            readOnly
              ? undefined
              : "What this connection is for, so the next person does not have to guess."
          }
          validationState={descriptionMessage ? "error" : "none"}
          validationMessage={descriptionMessage}
          onChange={(event) => {
            if (refusedFields.description) {
              setRefusedFields(({ description: _retyped, ...rest }) => rest);
            }
            setForm((previous) =>
              previous ? { ...previous, description: event.target.value } : previous,
            );
          }}
        />

        {renderVersionField()}

        {renderRefreshNote()}

        {!isNew && dataflowId ? (
          <SchemaPolicySection
            dataflowId={dataflowId}
            mode={detail?.schemaPolicy}
            canWrite={canWrite}
          />
        ) : null}

        <FieldGroups
          groups={definition.fieldGroups}
          fields={definition.fieldDefinitions}
          values={form.values}
          fieldState={fieldState}
          errors={fieldErrors}
          showErrors={showErrors}
          touched={touchedFields}
          onFieldBlur={touchField}
          readOnly={readOnly}
          onChange={setValue}
          onBrowse={dataflowId ? setBrowseFieldId : undefined}
          groupFooter={credentialGroupFooter}
          withheld={withheldFields}
          pinned={pinnedFields}
          pinnedChanged={pinnedChanged}
        />
      </div>
    </section>
  );

  /** #2428: a GA4 connection's Reports: empty until a report is added, each report one table. */
  const renderReports = () => (
    <ReportList
      rows={grid.rows}
      selectedCount={grid.selectedCount}
      total={grid.total}
      readOnly={readOnly}
      naming={entityNaming}
      onToggle={toggleRows}
      {...(readOnly ? {} : { onAdd: () => setReportDrawer({ mode: "add", from: null }) })}
      onEdit={(name) => setReportDrawer({ mode: "edit", from: name })}
      onDuplicate={(name) => setReportDrawer({ mode: "add", from: name })}
      onRemove={removeReport}
      onView={(name) => setReportDrawer({ mode: "view", from: name })}
      page={entityQuery.page}
      pageSize={pageSize}
      onPageChange={(page) => setQuery({ page })}
      isLoading={!isNew && entityPage.isFetching}
    />
  );

  const renderEntitiesSection = () => (
    <section aria-labelledby="connection-entities-heading" className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SkyText
          as="h3"
          id="connection-entities-heading"
          ref={entitiesHeading}
          tabIndex={-1}
          size={300}
          weight="semibold"
          className="outline-none"
        >
          {entitySelection?.entityLabelPlural ?? "Entities"}
        </SkyText>
        <SkyText size={200} color="muted">
          {selectedCount.toLocaleString()} selected
        </SkyText>
      </div>

      {reportKind ? renderReports() : renderEntityList()}
    </section>
  );

  /** A saved connection's bulk table buttons write at once, so they are off while a save would be. */
  const bulkSaveProps = () => (isNew ? {} : bulkOff(saveOffReason(importLive, isValidating)));

  const renderEntityList = () =>
    catalogueIsEmpty ? (
      renderEmptyCatalogue()
    ) : (
      <EntityTable
        rows={grid.rows}
        total={grid.total}
        entityCount={grid.entityCount}
        selectedCount={grid.selectedCount}
        page={entityQuery.page}
        pageSize={pageSize}
        onPageChange={(page) => setQuery({ page })}
        search={entityQuery.search}
        onSearchChange={(search) => setQuery({ search, page: 1 })}
        view={entityView}
        onViewChange={(selected) => setQuery({ selected, page: 1 })}
        entityType={entityTypeFilter}
        onEntityTypeChange={(entityType) => setQuery({ entityType, page: 1 })}
        viewCount={grid.viewCount}
        selectedViewCount={grid.selectedViewCount}
        isLoading={!isNew && entityPage.isFetching}
        onToggle={toggleRows}
        onRowChange={changeRow}
        onSelectAllMatching={selectAllMatching}
        bulkSaves={!isNew}
        {...bulkSaveProps()}
        config={entitySelection}
        supportsIncrementalPerEntity={supportsIncremental}
        readOnly={readOnly}
        listedAt={validation?.at ?? null}
        {...entityTableOptions()}
        onAddEntity={
          entitySelection?.supportsAddEntity && entitySelection.addEntityFields?.length
            ? () => setAddEntityOpen(true)
            : undefined
        }
        destination={tableNaming.destination}
        naming={entityNaming}
      />
    );

  // #927: the last Validate's answer, beside the button. Its key is the answer's time only, so
  // focus moves to a new answer once and never while the person types after it.
  const validationResult = ((): SaveBarValidationResult | null => {
    if (!lastValidate) return null;
    const key = `${lastValidate.at}:${lastValidate.status}`;
    // #1092: a failure about values since changed (a missing field now filled) is not repeated
    // as if it still held; the bar asks for a new Validate instead.
    if (changedSinceValidate) {
      return {
        key,
        intent: "warning",
        text: isNew
          ? "Changed since validation: validate again. Until then, Save keeps the setup incomplete."
          : "Changed since validation: validate again.",
      };
    }
    if (lastValidate.noVerdict) {
      return {
        key,
        // #2439: in the error colour, so it is not read as a passing remark.
        intent: "danger",
        text: validationSentence(lastValidate.message, definition.fieldDefinitions),
      };
    }
    if (lastValidate.status === "failed") {
      const sentence = validationSentence(lastValidate.message, definition.fieldDefinitions);
      return {
        key,
        intent: "danger",
        // #1098: a test of unsaved values says that the saved connection did not move, and, when
        // the save would test them again, what saving them does.
        text: lastValidate.unsaved
          ? [UNSAVED_TEST_FAILED, sentence, ...(saveRetests ? [UNSAVED_TEST_FAILED_SAVE] : [])]
              .filter(Boolean)
              .join(" ")
          : `Validation failed. ${sentence}`,
      };
    }
    const removedLine = lastValidate.removedSelected
      ? removedSelectedSentence(lastValidate.removedSelected, entityLabelsOf(definition))
      : null;
    return {
      key,
      intent: "success",
      text: isNew
        ? `Validation passed. Choose the ${entityPluralLower} to import, then save.`
        : unsavedPassText(lastValidate.unsaved, saveRetests, entityPluralLower),
      ...(removedLine ? { extra: removedLine } : {}),
    };
  })();

  const renderSaveArea = () => (
    <>
      {/* A failed save or validation, directly above the bar that holds Save and Validate
          (ux-s10 §3 rule 2). Pinned WITH the bar, so it is in view wherever the form is
          scrolled; the bar's own `sticky` then holds inside this wrapper. */}
      <div
        className={saveAreaClass(readOnly, Boolean(formError.error))}
        data-testid="connection-save-area"
      >
        {formError.error ? (
          <div className={SAVE_ERROR_PANEL_CLASS} data-testid="connection-save-error-panel">
            {/* Always titled by what failed, a retryable failure too (rehearsal F26): a hub 503
                read "Try again" over a generic sentence, and the person could not tell it was
                the validation that had failed. The server's message says when to retry. */}
            <InlineErrorBar
              error={{ ...formError.error, details: panelLines }}
              title={formErrorTitle}
              onDismiss={formError.clear}
              testId="connection-save-error"
            />
          </div>
        ) : null}
        {readOnly ? null : (
          <SaveBar
            need={setupNeed}
            state={state}
            status={saveStatus}
            isDirty={isDirty}
            isSaving={isSaving}
            isValidating={isValidating}
            saveChecks={isNew ? validatedUnchanged : saveRetests}
            canValidate={canValidate}
            validateLabel={storedCredential ? REVALIDATE_LABEL : VALIDATE_CONNECTION_LABEL}
            onSave={handleSave}
            {...saveBlocked(
              nameCollisions.size > 0,
              targetNameInvalid,
              entityPluralLower,
              displayNameProblem !== null && !displayNameMissing,
              descriptionTooLong,
            )}
            {...saveDisabled(importLive, isValidating, displayNameMissing)}
            onValidate={() => handleValidate("bar")}
            validationResult={validationResult}
            onDiscard={() => {
              if (baseline) {
                seed(baseline);
                // Review of #2429: a pinned field is not the person's edit, so Discard keeps it and
                // the field never shows a value the form does not hold.
                if (pinnedFields.size > 0) {
                  setForm({ ...baseline, values: withPinnedValues(baseline.values, pinnedFields) });
                }
              }
              // #1647: an unsaved Validate result belongs to the edit just thrown away.
              setValidation(detail ? detail.lastValidation : null);
              setShowErrors(false);
              // #1092: the refusal was about the edit just thrown away.
              clearRefusal();
            }}
          />
        )}
      </div>
    </>
  );

  const renderDialogs = () => (
    <>
      <CancelImportDialog target={cancelTarget} onClose={() => setCancelTarget(null)} />

      {tableRun && dataflowId ? (
        <TableRunDialog
          open
          onOpenChange={(open) => {
            if (!open) setTableRun(null);
          }}
          mode={tableRun.mode}
          dataflowId={dataflowId}
          connectionName={detail?.displayName ?? "this connection"}
          table={tableRun.table}
        />
      ) : null}

      {/* Mounted only while open, so the dialog starts fresh on each open. */}
      {importOptionsOpen && dataflowId && detail ? (
        <RunNowDialog
          open
          onOpenChange={setImportOptionsOpen}
          connections={[listedConnection ?? detail]}
          schedules={[]}
          initialTarget={{ kind: "dataflow", id: dataflowId }}
          allowKindChoice={false}
          targetFixed
        />
      ) : null}

      {schemaDrawer && dataflowId ? (
        <SchemaChangeDrawer
          open
          onClose={closeSchemaChanges}
          dataflowId={dataflowId}
          connectionName={detail?.displayName ?? "this connection"}
          entityName={schemaDrawer.entityName}
          canWrite={canWrite}
          canStart={canStartImport}
          {...initialStatusProp(schemaDrawer.initialStatus)}
          runId={schemaDrawer.runId ?? null}
        />
      ) : null}

      {reportKind && reportDrawer ? (
        <ReportDrawer
          open
          mode={reportDrawer.mode}
          initial={(() => {
            const row = reportDrawer.from ? reportRowOf(reportDrawer.from) : undefined;
            return row?.definition ? { name: row.name, definition: row.definition } : null;
          })()}
          propertyIdsText={String(form.values.propertyIds ?? "")}
          otherNames={[
            ...form.entities.map((entity) => entity.name),
            ...form.draft.added.map((entity) => entity.name),
            ...grid.rows.map((entity) => entity.name),
          ]}
          heldNames={detail?.heldReportNames ?? []}
          naming={entityNaming}
          historyDays={historyDaysOf(form.values)}
          startDate={String(form.values.startDate ?? "")}
          onSubmit={(report) => submitReport(reportDrawer.mode, report)}
          onClose={() => setReportDrawer(null)}
        />
      ) : null}

      <RemoveReportDialog
        target={removeTarget}
        onConfirm={confirmRemoveReport}
        onClose={() => setRemoveTarget(null)}
      />

      <RebuildReportsDialog
        open={rebuildLines !== null}
        lines={rebuildLines ?? []}
        onConfirm={() => handleSave({ rebuildConfirmed: true })}
        onClose={() => setRebuildLines(null)}
      />

      {entitySelection?.addEntityFields?.length && !reportKind ? (
        <AddEntityDrawer
          open={addEntityOpen}
          onClose={() => setAddEntityOpen(false)}
          addEntityFields={entitySelection.addEntityFields}
          existingEntityNames={[
            ...form.entities.map((entity) => entity.name),
            ...form.draft.added.map((entity) => entity.name),
            ...grid.rows.map((entity) => entity.name),
          ]}
          entityLabelSingular={entitySelection.entityLabelSingular}
          onAdd={(entity) =>
            setForm((previous) => {
              if (!previous) return previous;
              if (isNew) {
                return { ...previous, entities: inEntityOrder([...previous.entities, entity]) };
              }
              return {
                ...previous,
                draft: { ...previous.draft, added: [...previous.draft.added, entity] },
              };
            })
          }
        />
      ) : null}

      {browseField && dataflowId ? (
        <BrowseDialog
          open
          onOpenChange={(open) => {
            if (!open) setBrowseFieldId(null);
          }}
          dataflowId={dataflowId}
          fields={changedFieldInputs()}
          initialPath={String(form.values[browseField.id] ?? "") || "/"}
          mode={
            entitySelection?.browsePicker?.modeSourceFieldId &&
            form.values[entitySelection.browsePicker.modeSourceFieldId] === "file"
              ? "file"
              : "folder"
          }
          onSelect={(path) => {
            setValue(browseField.id, path);
            setBrowseFieldId(null);
          }}
        />
      ) : null}
    </>
  );

  const renderFieldErrorsNote = () => (
    <>
      {/* Errors that the form engine found are shown on the fields; this states
          the same thing once, for anyone who saved from the bottom of a long
          form and cannot see which field went red. */}
      {tab === "configuration" && showErrors && hasFieldErrors(fieldErrors) ? (
        <div className="mt-3">
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="form-has-errors"
            title="Some fields still need attention"
            message={
              savedOk && !isDirty
                ? "The highlighted fields are incomplete. Your changes are saved either way. The setup stays incomplete until it validates."
                : "The highlighted fields are incomplete. The setup stays incomplete until it validates."
            }
          />
        </div>
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col" data-testid="connection-editor">
      {renderTopbar()}

      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="connection-title-block">
        {/* Session 11 (P1): the connector's logo at 28 px; its name stays beside the title. */}
        <ConnectorLogo iconUrl={definition.iconUrl} name={definition.displayName} />
        <SkyText as="h2" size={400} weight="semibold" testId="connection-title">
          {form.displayName || `New ${definition.displayName} connection`}
        </SkyText>
        <SkyText size={200} color="muted">
          {definition.displayName}
        </SkyText>
      </div>

      {toggle.dialog}

      {renderBanners()}

      {renderConnectionTabs()}

      {renderRunsPanel()}

      {/* Hidden, not unmounted, while the Runs view is open: the form keeps its scroll, its open
          groups and its entity page, and nothing is fetched again on the way back. */}
      <div hidden={tab === "runs"} data-testid="connection-tabpanel-configuration">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          {renderFormSection()}

          {renderEntitiesSection()}
        </div>

        {renderSaveArea()}
      </div>

      {renderDialogs()}

      {renderFieldErrorsNote()}
    </div>
  );
}
